import { useState, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "../../api/client";
import { formatColumnHeader, isHiddenColumn, formatCellValue, humanizeLabel } from "../../lib/reports";
import { fmtCurrency } from "../../lib/display-labels";
import { useAuth } from "../../contexts/AuthContext";
import SortTh, { sortCompare, makeToggleSort } from "../../components/SortTh";
import { CrewHoursTable } from "./CrewHoursTable";
import { crewDetailPath, crewHoursExport, readCrewHoursRow } from "../../lib/reports/crew-hours";
import { useOpenRecord } from "../downhole/recordPage";
import PageHeaderBand, { PageHeaderStat } from "../../components/layout/PageHeaderBand";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
} from "../../lib/load-failure";
import type { TenantRole } from "../../lib/roles";
import {
  Search,
  DollarSign,
  Users,
  Wrench,
  BarChart3,
  Calendar,
  FileText,
  Truck,
  Receipt,
  Clock,
  Package,
  MapPin,
  CheckCircle2,
  AlertTriangle,
  Download,
  ArrowLeft,
  Printer,
  ChevronRight,
  Lock,
  LayoutList,
  ExternalLink,
  LibraryBig,
  Rows3,
  Layers,
  CalendarRange,
  Shield,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import { calendarDayString, parseCalendarDate, tenantTodayDate, toCalendarDateString } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";

/* ─── Zod Response Schemas ─── */

// Verified API contract as of 2026-06-15:
// GET /api/v1/reports/by-equipment
// Response: { success: true, data: [{ id, name, type, serial_number,
//   hours_used, jobs_count, utilization_rate }, ...] }
const EquipmentUtilizationRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  serial_number: z.string().nullable(),
  hours_used: z.number(),
  jobs_count: z.number(),
  utilization_rate: z.number(),
}).strict();

const EquipmentUtilizationResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(EquipmentUtilizationRowSchema),
}).strict();

// Verified API contract as of 2026-05-21:
// GET /api/v1/reports/by-item-type
// Response: { success: true, data: [{ revenue_code, service_name,
//   description, ticket_number, ticket_date, customer_name,
//   quantity, unit_price, total }, ...] }
// Proof-of-contract: endpoint returns 404 as of 2026-05-21 (not yet
// deployed to production); schema matches the verified shape provided
// by the API team.
const ItemTypeRowSchema = z.object({
  revenue_code: z.string(),
  service_name: z.string(),
  description: z.string().nullable(),
  ticket_number: z.string(),
  ticket_date: z.string(),
  customer_name: z.string(),
  quantity: z.number(),
  unit_price: z.number(),
  total: z.number(),
}).strict();

const ItemTypeResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(ItemTypeRowSchema),
}).strict();

/** Generic envelope: { success: true, data: any[] } — covers flat-row reports */
const FlatDataResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(z.record(z.any())),
}).strict();

/** Grouped envelope: { success: true, groups: [...], grand_total?: object|number } */
const GroupedResponseSchema = z.object({
  success: z.literal(true),
  groups: z.array(z.any()),
  grandTotal: z.union([z.number(), z.record(z.any())]).optional(),
  grand_total: z.union([z.number(), z.record(z.any())]).optional(),
});

// Verified API contract as of 2026-05-21:
// GET /api/v1/reports/by-customer
// data.customers[0] keys: customer_id, customer_name, total_jobs, revenue_cad, revenue_usd, total_revenue, outstanding
// data.jobs[0] keys: id, job_number, well_name, rig_name, status, scheduled_date, customer_id, customer_name, job_revenue
const ByCustomerCustomerSchema = z.object({
  customer_id: z.union([z.string(), z.number()]),
  customer_name: z.string(),
  total_jobs: z.number(),
  revenue_cad: z.number(),
  revenue_usd: z.number(),
  total_revenue: z.number(),
  outstanding: z.number(),
}).strict();

const ByCustomerJobSchema = z.object({
  id: z.string(),
  job_number: z.string().nullable(),
  well_name: z.string().nullable(),
  rig_name: z.string().nullable(),
  status: z.string().nullable(),
  scheduled_date: z.string().nullable(),
  customer_id: z.union([z.string(), z.number()]),
  customer_name: z.string(),
  job_revenue: z.number(),
}).strict();

const ByCustomerResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    customers: z.array(ByCustomerCustomerSchema),
    jobs: z.array(ByCustomerJobSchema),
  }).passthrough(),
}).strict();

// Verified API contract as of 2026-08-13 (fieldiq-api #298):
// GET /api/v1/reports/by-date
// Response: { success: true, data: { rows|tickets: [{ work_date, ticket_number,
//   job_number, customer_name, rig_name, status, currency, subtotal,
//   revenue_cad, revenue_usd, ticket_hours }, ...],
//   grand_total: { total_revenue, revenue_cad, revenue_usd, ticket_hours, total_tickets } } }
// `ticket_hours` was named `crew_hours` before #298. Crew-weighted exposure
// hours land next, and would differ from these unweighted ticket hours by 3-5x.
const ByDateTicketSchema = z.object({
  work_date: z.string(),
  ticket_number: z.string().nullable().optional(),
  job_number: z.string().nullable().optional(),
  customer_name: z.string().nullable().optional(),
  rig_name: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  subtotal: z.number(),
  revenue_cad: z.number().optional(),
  revenue_usd: z.number().optional(),
  ticket_hours: z.number().optional(),
  // TODO: remove crew_hours fallback — API renamed in #298
  crew_hours: z.number().optional(),
}).passthrough();

const ByDateGrandTotalSchema = z.object({
  total_revenue: z.number().optional(),
  revenue_cad: z.number().optional(),
  revenue_usd: z.number().optional(),
  ticket_hours: z.number().optional(),
  // TODO: remove crew_hours fallback — API renamed in #298
  crew_hours: z.number().optional(),
  jobs_completed: z.number().optional(),
}).passthrough();

const ByDateResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    tickets: z.array(ByDateTicketSchema),
    grand_total: ByDateGrandTotalSchema.optional(),
  }).passthrough(),
});

/**
 * Ticket hours off a by-date row or grand_total, across the #298 rename.
 * Returns null (never 0) when absent, so callers render "—" rather than a
 * plausible-looking zero.
 *
 * Single chokepoint on purpose: the deploy-gap fallback is one line to delete
 * rather than five scattered call sites to find.
 * TODO: remove crew_hours fallback — API renamed in #298
 */
function ticketHoursOf(o: any): number | null {
  const h = o?.ticketHours ?? o?.ticket_hours ?? o?.crew_hours;
  return h == null ? null : Number(h);
}

/**
 * Parse the API response body for a report endpoint.
 * All endpoints return { success: true, ... }.
 * Flat-row endpoints put rows in `data`; grouped endpoints use `groups`.
 */
function parseReportResponse(reportId: string, body: unknown): Record<string, any> {
  // Equipment Utilization — strict validation
  if (reportId === "ops-equipment") {
    const result = EquipmentUtilizationResponseSchema.safeParse(body);
    if (result.success) {
      return { rows: result.data.data };
    }
    console.error("[EquipmentUtilizationReport] Schema mismatch:", JSON.stringify(body, null, 2));
    return { _parseError: true };
  }

  // Revenue by Item Type — strict validation
  if (reportId === "rev-item-type") {
    const result = ItemTypeResponseSchema.safeParse(body);
    if (result.success) {
      return { rows: result.data.data };
    }
    console.error("[ItemTypeReport] Schema mismatch:", JSON.stringify(body, null, 2));
    return { _parseError: true };
  }

  // Revenue by Customer — grouped display with jobs per customer
  // rev-customer-detail uses the same /reports/by-customer endpoint
  if (reportId === "rev-customer" || reportId === "rev-customer-detail") {
    const parsed = ByCustomerResponseSchema.safeParse(body);
    if (!parsed.success) {
      console.error("[RevenueByCustomerReport] Schema mismatch:", parsed.error, body);
      return { _parseError: true };
    }
    return { customers: parsed.data.data.customers, jobs: parsed.data.data.jobs };
  }

  // Tickets & Revenue by Rig — { success, data: [...], grand_total: {...} }
  // API returns flat rows in `data` with a separate `grand_total` object.
  // The `.strict()` on FlatDataResponseSchema rejects the extra grand_total key,
  // so we handle this explicitly.
  if (reportId === "rev-rig") {
    const b = body as any;
    if (b?.success) {
      const d = b.data;
      // data is an array of flat rows
      if (Array.isArray(d)) {
        // Items may be group objects (with lines array) or flat rows
        if (d.length > 0 && Array.isArray(d[0]?.lines)) {
          return { groups: d, grandTotal: b.grand_total ?? b.grandTotal };
        }
        return { rows: d, grandTotal: b.grand_total ?? b.grandTotal };
      }
      // data may be a nested object with groups or rows
      if (d?.groups) {
        return { groups: d.groups, grandTotal: d.grand_total ?? b.grand_total };
      }
      if (Array.isArray(d?.rows)) {
        return { rows: d.rows, grandTotal: d.grand_total ?? b.grand_total };
      }
    }
    console.error("[TicketsByRigReport] Unexpected response shape:", JSON.stringify(body, null, 2));
    return { _parseError: true };
  }

  // Tickets & Revenue by Date — { success, data: { rows|tickets: [...], grand_total: {...} } }
  // API was updated: field is now `rows` instead of `tickets` — accept both.
  if (reportId === "rev-date") {
    const b = body as any;
    if (b?.success && b?.data) {
      const tickets = b.data.tickets || b.data.rows || [];
      const grandTotal = b.data.grand_total || b.data.grandTotal || {};
      return { tickets, dateGrandTotal: grandTotal };
    }
    console.error("[RevenueByDateReport] Unexpected response shape:", JSON.stringify(body, null, 2));
    return { _parseError: true };
  }

  // Try grouped envelope first
  const grouped = GroupedResponseSchema.safeParse(body);
  if (grouped.success) {
    const { success: _, ...rest } = grouped.data;
    return rest;
  }

  // Try flat data envelope
  const flat = FlatDataResponseSchema.safeParse(body);
  if (flat.success) {
    return { rows: flat.data.data };
  }

  // Fallback: log and flag
  console.error(`[UnifiedReports:${reportId}] Schema mismatch:`, JSON.stringify(body, null, 2));
  return { _parseError: true };
}

/* ─── The runner's register-level figures ─── */

/**
 * THE BAND AND THE BODY MUST NOT ANSWER THE SAME QUESTION TWICE, AND BEFORE
 * THIS THEY DID — DIFFERENTLY.
 *
 * `runnerStats` counted `reportResults.rows / groups / tickets` and read the
 * grand total off `grandTotal.total_revenue`. Neither is what every parse
 * branch actually produces, so the band disagreed with the table beneath it on
 * FOUR of the six runnable reports on a casing tenant. Measured against the
 * live API on Smith Casing, 2026-09-20, for 2026-09-01..2026-09-30:
 *
 *   report          band read                     body showed
 *   rev-customer    Records 0, Total —, Groups —  2 customers, 4 jobs, $28,003.40
 *   rev-rig         Total —                       Grand Total $28,003.40
 *   rev-date        Total —                       Grand Total CAD $34,003.40
 *   rev-item-type   Groups —                      grouped by revenue code
 *
 * `rev-customer` parses to `{customers, jobs}` and `rev-date` to `{tickets,
 * dateGrandTotal}` — neither key is one the band was looking at. `rev-rig`'s
 * grand total is `{total_billed}`, not `{total_revenue}`. None of this throws,
 * nothing logs, and the em dashes read as the em-dash rule working correctly.
 *
 * So the figures are derived ONCE, here, and both the band and the body's
 * grand-total lines read the result. A number that cannot be derived is `null`
 * and renders "—"; it is never coalesced to 0, because "$0.00" over a report
 * that has no grand total is a claim the payload never made.
 *
 * `total` is a SUM OF ROWS the API sent only where the API sends no total of
 * its own (rev-customer, rev-item-type) — which is what the body already did
 * at both those sites. That is not a second answer to the API's question; it
 * is the only answer available, and it now exists once instead of twice.
 */
interface ReportSummary {
  /** Rows in the result, however the shape spells them. */
  records: number;
  /** Subtotalled sections, or null when the report is not grouped at all. */
  groups: number | null;
  /** The report's grand total, or null when it has none. Never 0-for-absent. */
  total: number | null;
}

/** A finite number off any of `keys`, in order, else null. Absent stays absent. */
function firstFiniteKey(o: any, keys: string[]): number | null {
  if (o == null || typeof o !== "object") return null;
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Sum a column across rows. Returns null when no row carried a finite value. */
function sumOf(rows: any[], pick: (r: any) => any): number | null {
  let seen = false;
  let acc = 0;
  for (const r of rows) {
    const n = Number(pick(r));
    if (Number.isFinite(n)) {
      seen = true;
      acc += n;
    }
  }
  return seen ? acc : null;
}

function deriveReportSummary(res: Record<string, any> | null | undefined): ReportSummary {
  const empty: ReportSummary = { records: 0, groups: null, total: null };
  if (!res || res._parseError) return empty;

  const type = res.type;
  const rows: any[] = res.rows || res.data || [];
  const groups: any[] = res.groups || [];
  const tickets: any[] = res.tickets || [];
  const customers: any[] = res.customers || [];
  const jobs: any[] = res.jobs || [];

  // Revenue by Customer — parses to {customers, jobs}; the body groups the
  // jobs under the customers and sums `total_revenue` for its Report Total.
  if (type === "rev-customer" || type === "rev-customer-detail") {
    const groupCount = customers.length > 0
      ? customers.length
      : new Set(jobs.map((j: any) => j.customer_name || "Unknown")).size;
    const total = customers.length > 0
      ? sumOf(customers, (c) => c.total_revenue)
      : sumOf(jobs, (j) => j.job_revenue);
    return { records: jobs.length, groups: groupCount || null, total };
  }

  // Tickets & Revenue by Date — parses to {tickets, dateGrandTotal}. The
  // API's own grand total, under the key the by-date parse put it.
  if (type === "rev-date") {
    return {
      records: tickets.length,
      groups: null,
      total: firstFiniteKey(res.dateGrandTotal, ["total_revenue", "revenue_cad"]),
    };
  }

  // Revenue by Item Type — flat rows the body groups by `revenue_code`.
  if (type === "rev-item-type") {
    return {
      records: rows.length,
      groups: new Set(rows.map((r: any) => r.revenue_code)).size || null,
      total: sumOf(rows, (r) => r.total),
    };
  }

  // Equipment Utilization — rows, no money, no groups. Its `total` stays null
  // so the band says "—" rather than inventing a dollar figure for a report
  // that has no revenue column at all.
  if (type === "ops-equipment") {
    return { records: rows.length, groups: null, total: null };
  }

  // Everything else: grouped (by-rig and the generic grouped envelope) or flat.
  // `grand_total` arrives as a number on the legacy shape and as an object on
  // by-rig — where its key is `total_billed`, not `total_revenue`.
  const rawGT = res.grandTotal ?? res.grand_total;
  const total = typeof rawGT === "number" && Number.isFinite(rawGT)
    ? rawGT
    : firstFiniteKey(rawGT, ["total_billed", "total_revenue", "total"]);

  if (groups.length > 0) {
    const lines = groups.reduce(
      (n: number, g: any) => n + (Array.isArray(g.lines) ? g.lines.length : 0),
      0,
    );
    return { records: lines, groups: groups.length, total };
  }
  return { records: rows.length, groups: null, total };
}

/* ─── by-rig line helpers ─── */

/**
 * GET /reports/by-well SENDS `equipment` AND `crew` AS ARRAYS OF OBJECTS, AND
 * RENDERING ONE DIRECTLY TAKES THE WHOLE ROUTE DOWN.
 *
 * Each line carries `equipment: [{job_id, equipment_id, equipment_name,
 * equipment_type}, …]` and `crew: [{job_id, user_id, first_name, last_name,
 * initials, role_on_job}, …]`. The cells below read `r.equipment_name ||
 * r.equipment`; `equipment_name` is not a key on a line, so the `||` fell
 * through to the array and handed React a list of plain objects —
 * "Objects are not valid as a React child (found: object with keys {job_id,
 * equipment_id, equipment_name, equipment_type})", thrown during render. There
 * is no error boundary on the route, so the reader gets a BLANK SCREEN, not a
 * bad cell.
 *
 * Why it reads as "this whole report is broken": an EMPTY array is a perfectly
 * valid React child and renders nothing at all, so a tenant whose jobs carry no
 * equipment assignments never sees it. The crash fires only on the lines that
 * have something to show.
 *
 * Verified against the live payload on Smith Casing, 2026-09-20 (2 rig groups,
 * 5 lines, equipment and crew populated on both).
 */
function rigEquipmentLabel(r: any): string | null {
  if (typeof r?.equipment_name === "string" && r.equipment_name) return r.equipment_name;
  const list = r?.equipment;
  if (!Array.isArray(list)) return typeof list === "string" && list ? list : null;
  const names = list
    .map((e: any) => (typeof e === "string" ? e : e?.equipment_name))
    .filter((n: any): n is string => typeof n === "string" && n.length > 0);
  return names.length > 0 ? names.join(", ") : null;
}

/**
 * The crew names off a by-well line. `users.first_name`, `last_name` and
 * `initials` are ALL nullable in production, so a member can yield no name at
 * all; those are dropped rather than rendered as a blank comma, and a line
 * whose every member is nameless renders "—" — absent, not an empty list.
 */
function rigCrewLabel(r: any): string | null {
  if (typeof r?.crew_name === "string" && r.crew_name) return r.crew_name;
  const list = r?.crew;
  if (!Array.isArray(list)) return typeof list === "string" && list ? list : null;
  const names = list
    .map((c: any) => {
      if (typeof c === "string") return c;
      const full = [c?.first_name, c?.last_name].filter(Boolean).join(" ").trim();
      return full || c?.initials || "";
    })
    .filter((n: any): n is string => typeof n === "string" && n.length > 0);
  return names.length > 0 ? names.join(", ") : null;
}

/**
 * The billed amount off a by-well line. The API's key is `total_billed`; the
 * three spellings after it are older shapes kept as fallbacks.
 *
 * THIS IS A RENAME, NOT A RE-BIND. `total_billed` is the same fact `billed`
 * named — the group's `subtotal` is the sum of exactly these — so it is the
 * first case in CLAUDE.md's "an em dash over a money column" rule, the one
 * where repairing the spelling is correct. Every cell in this column rendered
 * "—" while the subtotal beside it showed a real figure.
 */
function rigBilled(r: any): number | null {
  const v = r?.total_billed ?? r?.billed ?? r?.amount ?? r?.total;
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/* ─── Report Template Definitions ─── */

/**
 * Which role gate the row's DATA sits behind, mirrored from the API's
 * `src/routes/reports.routes.ts` (api#741). Four groups there; three matter to
 * a catalog row:
 *
 *   financial  FINANCIAL_READ / CREW_SCHEDULE_READ — admin, office_staff,
 *              supervisor, dispatcher. `surface-rentals.routes.ts` spells the
 *              same four as RENTAL_BILLING_READ, so the rentals revenue and
 *              maintenance-cost rows mirror through this one too (api#764).
 *   pay        PAY_READ — admin, office_staff
 *   open       OPERATIONAL_READ — `noRoleGate(...)`, every authenticated role,
 *              field_worker included, because a hand on site needs a tool's
 *              cert days. On the rentals side the analogue is
 *              RENTALS_OPERATIONAL_READ, which after api#764 covers exactly
 *              ONE report endpoint — yard-movement, the one that carries no
 *              money. Also `/equipment-maintenance/*`, which is outside the
 *              /reports groups entirely.
 *
 * This is a MIRROR of a gate that lives on the other side of the wire. It
 * decides what the catalog SAYS, never what the caller gets — the API refuses
 * on its own. Keeping it here is what lets a row the caller cannot run say so
 * instead of 403-ing on click.
 *
 * BUT THE QUESTION THIS FIELD ANSWERS IS "CAN THIS CALLER GET A TRUE ANSWER",
 * NOT "WOULD THE CALL RETURN 200", AND THE TWO COME APART. An endpoint that
 * NARROWS by role — 200, but only the caller's own rows — hands a screen a
 * real result set that is the wrong population. Aggregate it and head it with
 * a tenant-wide title and you get a plausible wrong number: true arithmetic,
 * real rows, no error, and nothing on screen that could tell the reader the
 * denominator was cut. That is strictly worse than the 403 a refused row
 * produces, because the 403 is visible and this is not.
 *
 * So a row whose screen asks a TENANT-WIDE question off a NARROWING endpoint
 * carries the gate of the roles that get the whole population, even though a
 * narrowed role could technically run it. `rev-ar-aging` is the worked example
 * and the only current one; its comment at the call site has the reasoning.
 * The rule for the next row: read what the handler DOES to the result set, not
 * just the requireRole list at the door.
 */
type ReportGate = "open" | "financial" | "pay";

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  category: "revenue" | "operations" | "payroll" | "safety";
  icon: React.ElementType;
  endpoint: string;
  route?: string;
  /**
   * Show this row only to a tenant with AT LEAST ONE of these verticals
   * enabled. Absent means cross-vertical, which is what every casing-era row
   * here is.
   *
   * A list rather than a single key because a row's data can come from more
   * than one vertical: `rev-customer-cross` reads trucking invoices AND rental
   * invoices, and a tenant with either one has something to see. Each entry is
   * an independent POSITIVE check OR'd together — never `!hasVertical`, which
   * hides a row from the multi-vertical tenant it is most useful to.
   */
  verticals?: string[];
  /** See ReportGate. Required, so a new row cannot forget to declare one. */
  gate: ReportGate;
}

const reportTemplates: ReportTemplate[] = [
  // CASING-ONLY BY WHAT THE HANDLER READS (traced 2026-10-08), not by name.
  // Each row below tagged ["casing_running"] runs an endpoint whose rows come
  // only from casing `tickets` / `ticket_line_items` / `jobs` /
  // `job_crew_assignments` / `job_equipment_assignments`: by-customer,
  // by-item-type, by-well, by-date, by-crew, by-equipment (equipment is shared
  // but its utilization is counted on casing jobs), financial, and AR aging
  // (lib/ar-by-customer.ts ages approved casing tickets). A pressure-testing or
  // downhole tenant got a report that is empty by construction. Payroll Summary
  // reads employee_timesheets AND trucking payroll, so it carries both keys.
  // Untagged on purpose: Equipment Maintenance (equipment_maintenance_records,
  // cross-vertical) and Safety Analytics (every tenant has safety).
  { id: "rev-customer", name: "Revenue by Customer", description: "Ticket list & revenue grouped by customer for a date range", category: "revenue", icon: Users, endpoint: "/reports/by-customer", verticals: ["casing_running"], gate: "financial" },
  { id: "rev-item-type", name: "Revenue by Item Type", description: "Line-item revenue history by service type with quantities & rates", category: "revenue", icon: DollarSign, endpoint: "/reports/by-item-type", verticals: ["casing_running"], gate: "financial" },
  { id: "rev-rig", name: "Tickets & Revenue by Rig", description: "All tickets and revenue grouped by rig/well for a date range", category: "revenue", icon: BarChart3, endpoint: "/reports/by-well", verticals: ["casing_running"], gate: "financial" },
  { id: "rev-date", name: "Tickets & Revenue by Date", description: "Chronological list of all tickets with revenue totals", category: "revenue", icon: Calendar, endpoint: "/reports/by-date", verticals: ["casing_running"], gate: "financial" },
  { id: "rev-customer-detail", name: "Ticket Revenue by Customer", description: "Detailed ticket-level revenue breakdown for a specific customer", category: "revenue", icon: Receipt, endpoint: "/reports/by-customer", gate: "financial" },
  // by-crew is PAY_READ, not OPERATIONAL — it is filed under Operations here
  // but the API reads it as pay data, so a supervisor is refused it.
  // Earthworks: clock-ins linked to an EWJ job count as field hours there
  // (api crew-hours.service, "by SOURCE"), so the report is not empty for it.
  { id: "ops-hours", name: "Hours & Jobs by Employee", description: "Employee hours worked and jobs completed by date range", category: "operations", icon: Clock, endpoint: "/reports/by-crew", verticals: ["casing_running", "earthworks"], gate: "pay" },
  { id: "ops-equipment", name: "Equipment Utilization Report", description: "Equipment deployment days, jobs, and utilization rates", category: "operations", icon: Wrench, endpoint: "/reports/by-equipment", verticals: ["casing_running"], gate: "open" },
  { id: "ops-equipment-maint", name: "Equipment Maintenance", description: "Maintenance schedules, costs, and upcoming services", category: "operations", icon: Wrench, endpoint: "", route: "/reports/equipment-maintenance", gate: "open" },
  { id: "pay-timesheets", name: "All Timesheets for Pay Period", description: "Complete timesheet data for a selected pay period", category: "payroll", icon: FileText, endpoint: "/reports/payroll-timesheets", gate: "pay" },
  { id: "pay-bonus", name: "Bonus & Mileage by Employee", description: "Bonus payments and mileage paid to employees by province", category: "payroll", icon: Truck, endpoint: "/reports/payroll-bonus", gate: "pay" },

  // Six pages that were routed under /reports/* with no catalog row and no nav
  // entry anywhere else — reachable only by typing the URL. They render their
  // own screen rather than this page's generic table, so they carry `route`
  // and an empty `endpoint`, the same shape ops-equipment-maint already uses.
  //
  // THE RENTALS ROWS NO LONGER SHARE ONE GATE, AND THE ODD ONE OUT IS THE
  // POINT. These four were all `open` because the surface-rentals report
  // endpoints carried no role gate at all — recorded here as a gap on the API
  // side, reported rather than worked around. api#764 closed it, and closed it
  // per endpoint rather than per file: three of the four now carry
  // RENTAL_BILLING_READ, which is the same four roles as FINANCIAL_READ.
  //
  // `rentals-yard-movement` KEEPS `open` deliberately. api#764 left it on
  // RENTALS_OPERATIONAL_READ (`noRoleGate`) because it reports assets in and
  // out of the yard and carries no money — the same reasoning that leaves
  // equipment status and cert usage open to a hand. Read off
  // `surface-rentals.routes.ts` at 46e079f, one line per route; do NOT
  // regularise it to match its three siblings.
  { id: "rentals-revenue-summary", name: "Rental Revenue Summary", description: "Month-by-month rental revenue for a year, with totals", category: "revenue", icon: DollarSign, endpoint: "", route: "/reports/revenue-summary", verticals: ["surface_rentals"], gate: "financial" },
  { id: "rentals-revenue-by-asset", name: "Rental Revenue by Asset", description: "Revenue and utilization per rental asset for a date range", category: "revenue", icon: Package, endpoint: "", route: "/reports/revenue-by-asset", verticals: ["surface_rentals"], gate: "financial" },
  { id: "rentals-yard-movement", name: "Yard Movement", description: "Assets in and out of the yard over a date range", category: "operations", icon: MapPin, endpoint: "", route: "/reports/yard-movement", verticals: ["surface_rentals"], gate: "open" },
  { id: "rentals-maintenance-summary", name: "Rental Maintenance Summary", description: "Work orders, costs, and downtime by asset", category: "operations", icon: Wrench, endpoint: "", route: "/reports/maintenance-summary", verticals: ["surface_rentals"], gate: "financial" },
  { id: "trucking-driver-pay-detail", name: "Driver Pay Detail", description: "Per-driver pay breakdown by BOL for a date range", category: "payroll", icon: Truck, endpoint: "", route: "/reports/driver-pay-detail", verticals: ["trucking"], gate: "pay" },
  // Three endpoints behind one screen: bol-summary and customer-revenue are
  // FINANCIAL_READ, driver-pay is PAY_READ. The row carries the gate that gets
  // you THROUGH THE DOOR; the pay tab refuses on its own inside.
  { id: "trucking-reports", name: "Trucking Reports", description: "BOL summary, driver pay, and customer revenue for a date range", category: "operations", icon: Truck, endpoint: "", route: "/trucking/reports", verticals: ["trucking"], gate: "financial" },
  // One screen, three reports, like the trucking row above. Gate "open": the
  // downhole read surface admits admin, office, dispatch and field roles alike
  // (api downhole.routes.ts DH_READ), and the row should not refuse a reader
  // the API would serve.
  { id: "downhole-reports", name: "Downhole Tool Reports", description: "Component usage, assembly history, and work orders for a date range", category: "operations", icon: Wrench, endpoint: "", route: "/downhole/reports", verticals: ["downhole_tools"], gate: "open" },

  // Six more pages whose ONLY inbound link was `screens/ReportsScreen.tsx` —
  // the second reports catalog, routed at /safety/reports. Same `route` +
  // empty `endpoint` shape as the group above: every one renders its own
  // screen, none runs in this page's own runner.
  //
  // THAT SCREEN IS NOW DELETED (fe#762), AND THE ORDER IS THE POINT. These
  // rows landed in fe#719, BEFORE the deletion, and the two shipped as
  // separate PRs on purpose: carrying a page's row here first is what keeps
  // the page reachable across the change, so there is never a commit in which
  // it is routed and linked from nothing. Do the same for the next one — a
  // deletion that removes the last linker and adds the replacement row in one
  // diff is correct only if the row is right, and nothing checks that but the
  // reviewer.
  //
  // Two of ReportsScreen's rows are NOT here, and were deleted with it rather
  // than carried: /reports/customer ran GET /reports/by-customer, which is
  // `rev-customer` above under the same name, and /reports/equipment was
  // titled "Equipment Utilization" while tallying `api.equipment.list()`
  // client-side — the question `ops-equipment` answers from the API, and the
  // register /equipment already renders properly. See the route declarations
  // in App.tsx for the full accounting of all 18 of its rows.
  // BACK IN THE CATALOG (fe#760). It was withdrawn because
  // GET /reports/payroll-summary summed `timesheets.gross_pay`, a retired
  // column that is NULL by design, so every operations worker read $0.00 for
  // every range that would ever exist. api#794 repointed it at
  // `employee_timesheets`, where pay actually lives, and the screen now reads
  // that shape: operations and trucking side by side, committed and draft
  // totalled separately, and no combined total anywhere.
  //
  // `gate: "pay"` because the route is PAY_READ, unchanged by api#794.
  { id: "pay-summary", name: "Payroll Summary", description: "Every worker's earnings for a date range, with company totals", category: "payroll", icon: DollarSign, endpoint: "", route: "/reports/payroll-summary", verticals: ["casing_running", "trucking"], gate: "pay" },
  // `financial` because GET /reports/financial is FINANCIAL_READ. Cross-vertical
  // despite the screen's casing lineage — the endpoint has no vertical guard and
  // the route carries none either.
  { id: "rev-financial", name: "Financial Summary", description: "Revenue, cost, and margin for a date range", category: "revenue", icon: BarChart3, endpoint: "", route: "/reports/financial", verticals: ["casing_running"], gate: "financial" },
  // Reads GET /reports/customers then GET /reports/customer/:id — both
  // FINANCIAL_READ. The route below renders `pages/reports/CustomerReport.tsx`,
  // imported there under the alias `CrossCustomerReport`.
  //
  // THE SENTENCE HERE USED TO WARN AGAINST CONFUSING IT WITH
  // `pages/reports/CustomerReportUnified.tsx`, WHICH WAS IMPORTED NOWHERE AND
  // IS NOW DELETED (fe#762). The warning is kept because the hazard is not
  // gone with the file: three surviving modules still have near-identical
  // names for three different reports, and the alias is what hides it —
  //
  //   pages/reports/CustomerReport.tsx    this row, as `CrossCustomerReport`
  //   pages/rentals/CustomerReport.tsx    deleted in the trucking/rentals sweep
  //   screens/CustomerReportScreen.tsx    `rev-ar-aging`, at /reports/customers
  //
  // A sweep by FILE NAME hits the wrong one of those three, which is exactly
  // how the deleted file survived being unreferenced for as long as it did.
  // Resolve by import path, never by basename.
  //
  // TRUCKING OR RENTALS, AND IT SHIPPED WITH NO GATE AT ALL. This row landed
  // cross-vertical because its two endpoints carry no vertical guard — but a
  // guard on the ROUTE is not the question; what the HANDLER reads is.
  // `/reports/customers` selects from `trucking_invoices` and
  // `rental_invoices` only, and ends `WHERE (truck.revenue > 0 OR rent.revenue
  // > 0)`, so a tenant with neither vertical gets `[]` for every date range
  // that has ever existed. The screen has nothing to put in its customer
  // picker and renders an empty shell — which is what Smith Casing, a
  // casing-only tenant, saw: a catalog row promising a report that is blank by
  // construction, not by date range.
  //
  // Both keys, not one: the screen shows a trucking section and a rentals
  // section, and a tenant with either has real figures in one of them. That is
  // the `verticals` any-of, and it is why the field is a list.
  { id: "rev-customer-cross", name: "Cross-Customer Report", description: "Trucking and rental activity combined for one customer", category: "revenue", icon: Users, endpoint: "", route: "/reports/customer-unified", verticals: ["trucking", "surface_rentals"], gate: "financial" },
  // `financial`, AND THE REASON IS NOT "THE API WOULD REFUSE IT" — IT WOULD
  // NOT. This row is the reason this field is documented as a claim about
  // whether the caller can get a TRUE ANSWER, not about whether the call
  // returns 200.
  //
  // This screen (`screens/CustomerReportScreen.tsx`) derives per-customer
  // revenue and outstanding balance CLIENT-SIDE from GET /tickets. api#764
  // gated that route as `requireRole('admin', 'office_staff', 'supervisor',
  // 'dispatcher', 'field_worker')` — and it admits a hand deliberately, so the
  // expense ticket picker (`FieldExpenseCaptureScreen` → `useJobTickets`)
  // keeps working. But a hand does not get the tenant's ticket book. They fall
  // into the handler's `if (user.role === 'field_worker')` branch, which
  // narrows the rows to tickets they submitted or were assigned to.
  //
  // So a field_worker opening this screen gets a 200 and a REAL RENDER: their
  // own slice of the tickets, summed per customer and presented as the
  // company's receivables. Every figure on it is a true total of the wrong
  // population, under a heading that claims the whole tenant. That is a
  // plausible wrong number — the failure this file exists to prevent, arriving
  // through the one door nobody gates against, and it is strictly WORSE than
  // the 403 a `financial` row produces on click. Nothing on the screen can
  // signal it: the rows are real, the arithmetic is right, and the caller has
  // no way to know the denominator was cut.
  //
  // CAN CALL THE ENDPOINT IS NOT CAN RUN THE REPORT. The report asks a
  // tenant-wide question, and a field_worker cannot get a true answer to it.
  // The gate names who can, which is the four office roles — the same four the
  // sibling GET /reports/by-customer carries as FINANCIAL_READ, for exactly
  // the figures this screen re-derives.
  //
  // This is the one row where the mirror does NOT track the endpoint's gate
  // one-for-one, and the divergence is deliberate rather than drift. Do not
  // "correct" it back to `open` by reading the route's requireRole list: the
  // list is about admission, and this field is about truth. If the API ever
  // stops narrowing by role here — one question, one population — the row can
  // follow the gate again.
  //
  // THE ROW IS BACK IN THE CATALOG (fe#760), AND THE GATE ARGUMENT ABOVE NOW
  // MATCHES THE ENDPOINT RATHER THAN DIVERGING FROM IT. The screen no longer
  // derives anything from GET /tickets — it reads GET /reports/ar-aging
  // (api#796), which IS FINANCIAL_READ. So the `financial` value above, which
  // was a deliberate divergence from the route's own admission list, is now
  // simply the endpoint's gate. The reasoning is kept verbatim anyway: it is
  // the thing a future reader is most likely to "correct" back to `open` by
  // reading a requireRole list, and the distinction it draws — can call the
  // endpoint is not can run the report — outlives this particular screen.
  { id: "rev-ar-aging", name: "AR Aging", description: "Outstanding receivables per customer, aged 0-30 / 31-60 / 61-90 / 90+", category: "revenue", icon: Receipt, endpoint: "", route: "/reports/customers", verticals: ["casing_running"], gate: "financial" },
  // Two surface-rentals rows used to follow here, "Rental Reports" and
  // "Rental Equipment Status", pointing at /rentals/reports and
  // /rentals/reports/equipment. Both pages were deleted in the trucking/rentals
  // sweep: eight of the library's ten cards did nothing, and the equipment
  // page's totals string-concatenated NUMERIC columns. The four rentals rows
  // above are the working reports. The old paths redirect here.

  /* THE SAFETY ROW IS A `route`, AND THERE IS NO SECOND SAFETY ROW.
   *
   * The catalog used to carry a hidden `safety-incidents` template whose
   * endpoint, GET /reports/safety, was a stub answering `{ rows: [], message:
   * 'Report coming soon' }` for every tenant. api#804 deleted that route (it is
   * now a 404), so the template was deleted here rather than left hidden: a
   * hidden row is a thing someone unhides, and there is nothing behind this
   * one. Repointing it at GET /reports/analytics/safety would not have rescued
   * it either — that answers an OBJECT (trir, ytdHours, incidentsByMonth, …),
   * and this page's runner parses a rows/groups envelope.
   *
   *   this row                    the live endpoint already HAS a renderer:
   *                               `ReportsDashboard`'s Safety tab, which reads
   *                               TRIR through the shared `describeRate` map so
   *                               this figure and /safety/metrics cannot word
   *                               the same absent rate two ways. Nothing is
   *                               rebuilt or recomputed; the row opens the page
   *                               that exists.
   *
   * `gate: "open"` is read off the API, not assumed: `router.get('/analytics/safety',
   * authMiddleware, OPERATIONAL_READ, …)` in `src/routes/reports.routes.ts`, and
   * OPERATIONAL_READ is `noRoleGate(...)` — every authenticated role, field_worker
   * included. That is the right answer rather than an oversight: a hand on site
   * asking when the last incident was is the same argument that keeps cert days
   * open to everyone.
   *
   * The `?tab=safety` is what the route is FOR. `/reports/dashboard` is linked
   * from nothing in the app — this row is its first inbound link — and it opens
   * on Overview, so a row landing there with no tab would leave the reader to
   * find Safety themselves. ReportsDashboard reads the param and does nothing
   * else new; the page is otherwise untouched (its re-skin is parked).
   */
  { id: "safety-analytics", name: "Safety Analytics", description: "TRIR, days since last incident, meeting attendance, and incidents by month and type", category: "safety", icon: Shield, endpoint: "", route: "/reports/dashboard?tab=safety", gate: "open" },
];

/** Reports hidden from the UI — stubs or duplicates not ready for users. */
const HIDDEN_REPORT_IDS = [
  'rev-customer-detail',  // Duplicate of rev-customer
  'pay-timesheets',       // API stub — not yet implemented
  'pay-bonus',            // API stub — not yet implemented

  /* -- The two rows that WERE withdrawn here are back (fe#760) --
   *
   * `pay-summary` and `rev-ar-aging` were withdrawn because each put a
   * confident, plausible-looking money figure on screen that no tenant's data
   * could ever move: one summed a retired column that is NULL by design, the
   * other derived revenue client-side from a payload carrying no money key.
   * Neither was a bug in a screen — both were reports that were broken by
   * construction, which is why the fix had to be an API change and why the
   * catalog row came out rather than a defect being logged against a page.
   *
   * api#794 repointed payroll summary at `employee_timesheets`, and api#796
   * added `GET /reports/ar-aging` over the same partition `/reports/financial`
   * uses. Both screens now read those shapes. The reasoning that withdrew them
   * is not deleted with them: it lives on each screen, at the query, where a
   * reader who wonders why the page is shaped as it is will be standing.
   */
];

const visibleTemplates = reportTemplates.filter(
  (t) => !HIDDEN_REPORT_IDS.includes(t.id)
);

const categoryLabels: Record<string, string> = {
  revenue: "Revenue",
  operations: "Operations",
  payroll: "Payroll",
  safety: "Safety",
};

/**
 * Group order in the register, written down rather than inherited from the
 * order rows happen to sit in `reportTemplates`. Same sequence the page has
 * always rendered — Revenue, Operations, Payroll — now stated instead of
 * emergent, so moving a row in the array cannot silently reorder the page.
 */
const CATEGORY_ORDER = ["revenue", "operations", "payroll", "safety"] as const;

/** The chip row: "all" plus every category, in the register's own order. */
const CATEGORY_FILTERS = ["all", ...CATEGORY_ORDER];

/**
 * Who the API lets through each gate. Exhaustive over ReportGate — a new gate
 * fails the build here rather than defaulting a row to "runnable".
 *
 * `open` is null rather than the full role list on purpose: OPERATIONAL_READ
 * is `noRoleGate(...)`, an auditable opt-out rather than a list, and spelling
 * it as a list would silently start refusing a role added to the enum later.
 */
const GATE_ROLES: Record<ReportGate, TenantRole[] | null> = {
  open: null,
  financial: ["admin", "office_staff", "supervisor", "dispatcher"],
  pay: ["admin", "office_staff"],
};

/** The compact phrase naming who CAN run it, for the row that says no. */
const GATE_ALLOWED_LABEL: Record<ReportGate, string> = {
  open: "Everyone",
  financial: "Office & supervisors",
  pay: "Admin & office",
};

/**
 * THREE states, not two, and the third is the point.
 *
 *   'ok'        the caller's role is in the gate's list, or the gate is open
 *   'refused'   the caller's role is definitively outside it
 *   'unknown'   we cannot tell from what this screen has loaded
 *
 * 'unknown' exists for exactly one case: a `dispatcher` facing a `pay` gate.
 * Every gate on the API is NON-STRICT, so `roleAllows` elevates a dispatcher
 * into any list containing 'admin' when the tenant has `dispatcher_admin_access`
 * — and that flag is NOT on /auth/me (see the same note in EquipmentScreen).
 * Reading it would cost a query this page does not otherwise make.
 *
 * So the screen declines to claim. An unknown is NOT a refusal: marking a
 * dispatcher "you cannot run this" when the tenant may well have elevated them
 * is a confident claim about a row the screen could not classify — the same
 * mistake as Equipment's old page calling an unrecognised status "Available".
 * An unmarked row that 403s tells the caller the truth one click late; a
 * wrongly-marked row tells them a falsehood they cannot get past at all.
 */
type GateVerdict = "ok" | "refused" | "unknown";

function gateVerdict(gate: ReportGate, role: string | undefined): GateVerdict {
  const allowed = GATE_ROLES[gate];
  if (allowed === null) return "ok";
  if (!role) return "unknown";
  if (allowed.includes(role as TenantRole)) return "ok";
  if (role === "dispatcher" && allowed.includes("admin")) return "unknown";
  return "refused";
}

/* ─── Date helpers ───
 * Every range is anchored on the TENANT's today (companies.time_zone), never
 * the device's. With no known zone the range is empty — the date inputs stay
 * blank rather than seeded with a guessed day. */

function getThisWeekRange(timeZone: string | null): [string, string] {
  const now = tenantTodayDate(timeZone);
  if (!now) return ["", ""];
  const day = now.getDay();
  const diff = day === 0 ? 6 : day - 1; // Monday start
  const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return [fmt(mon), fmt(sun)];
}

function getThisMonthRange(timeZone: string | null): [string, string] {
  const now = tenantTodayDate(timeZone);
  if (!now) return ["", ""];
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return [fmt(start), fmt(end)];
}

function getYTDRange(timeZone: string | null): [string, string] {
  const now = tenantTodayDate(timeZone);
  if (!now) return ["", ""];
  return [`${now.getFullYear()}-01-01`, fmt(now)];
}

function fmt(d: Date): string {
  return toCalendarDateString(d);
}

/* ─── Export helpers ─── */

/** Build a generic { headers, rows } from the current report data */
function buildExportData(
  reportResults: Record<string, any> | null | undefined,
  timeZone: string | null,
): { headers: string[]; dataRows: string[][]; grandTotalLabel?: string; grandTotalValue?: string } {
  if (!reportResults) return { headers: [], dataRows: [] };

  const rows: any[] = reportResults.rows || reportResults.data || [];
  const groups: any[] = reportResults.groups || [];
  const grandTotal: number = reportResults.grandTotal ?? reportResults.grand_total ?? 0;
  const type = reportResults.type;

  // Hours & Jobs by Employee — the columns its screen draws, not one per key.
  if (type === "ops-hours" && rows.length > 0) {
    const { headers, rows: dataRows } = crewHoursExport(rows.map(readCrewHoursRow));
    return { headers, dataRows };
  }

  // Equipment Utilization — no revenue column
  if (type === "ops-equipment" && rows.length > 0) {
    const headers = ["Equipment", "Type", "Serial #", "Jobs", "Hours Used", "Utilization %"];
    const dataRows = rows.map((r: any) => [
      r.name, r.type, r.serial_number ?? "—",
      String(r.jobs_count), Number(r.hours_used).toFixed(1),
      Number(r.utilization_rate).toFixed(1) + "%",
    ]);
    return { headers, dataRows };
  }

  // Tickets & Revenue by Date — individual ticket rows + grand total
  if (type === "rev-date") {
    const tickets: any[] = reportResults.tickets || [];
    const gt: Record<string, any> = reportResults.dateGrandTotal || {};
    if (tickets.length === 0) return { headers: [], dataRows: [] };
    const headers = ["Date", "Ticket #", "Customer", "Rig / Well", "Status", "Ticket Hrs", "Revenue"];
    const dataRows = tickets.map((t: any) => [
      calendarDayString(t.work_date) || "—",
      t.ticket_number || t.job_number || "—",
      t.customer_name || "—",
      t.rig_name || "—",
      humanizeLabel(t.status || '') || "—",
      ticketHoursOf(t) != null ? ticketHoursOf(t)!.toFixed(1) : "—",
      (t.currency && t.currency !== "CAD" ? `${t.currency} ` : "") + fmtCurrency(t.subtotal),
    ]);
    const gtParts: string[] = [];
    if (gt.revenue_cad) gtParts.push(`CAD ${fmtCurrency(gt.revenue_cad)}`);
    if (gt.revenue_usd) gtParts.push(`USD ${fmtCurrency(gt.revenue_usd)}`);
    const gtValue = gtParts.length > 0 ? gtParts.join(" | ") : fmtCurrency(gt.total_revenue);
    return { headers, dataRows, grandTotalLabel: "Grand Total", grandTotalValue: gtValue };
  }

  // Revenue by Item Type — grouped by revenue_code
  if (type === "rev-item-type" && rows.length > 0) {
    const grouped = rows.reduce<Record<string, any[]>>((acc, r) => {
      const key = r.revenue_code;
      if (!acc[key]) acc[key] = [];
      acc[key].push(r);
      return acc;
    }, {});
    const headers = ["Revenue Code", "Service", "Ticket #", "Date", "Customer", "Description", "Qty", "Rate", "Amount"];
    const dataRows: string[][] = [];
    Object.entries(grouped).forEach(([code, items]) => {
      items.forEach((r: any) => {
        dataRows.push([
          code, r.service_name, r.ticket_number, r.ticket_date,
          r.customer_name, r.description ?? "—",
          String(r.quantity), fmtCurrency(r.unit_price), fmtCurrency(r.total),
        ]);
      });
      const subtotal = items.reduce((s: number, r: any) => s + Number(r.total), 0);
      dataRows.push(["", "", "", "", "", `Subtotal — ${code}`, "", "", fmtCurrency(subtotal)]);
    });
    const allTotal = deriveReportSummary(reportResults).total;
    return {
      headers,
      dataRows,
      grandTotalLabel: allTotal == null ? undefined : "Grand Total",
      grandTotalValue: allTotal == null ? undefined : fmtCurrency(allTotal),
    };
  }

  // Revenue by Customer
  if ((type === "rev-customer" || type === "rev-customer-detail") && (reportResults.customers?.length > 0 || reportResults.jobs?.length > 0)) {
    const customers: any[] = reportResults.customers || [];
    const jobs: any[] = reportResults.jobs || [];
    const jobsByCustomer: Record<string, any[]> = {};
    jobs.forEach((j: any) => {
      const name = j.customer_name || "Unknown";
      if (!jobsByCustomer[name]) jobsByCustomer[name] = [];
      jobsByCustomer[name].push(j);
    });
    const customerGroups = customers.length > 0
      ? customers.map((c: any) => ({
          name: c.customer_name,
          totalRevenue: c.total_revenue,
          jobs: jobsByCustomer[c.customer_name] || [],
        }))
      : Object.entries(jobsByCustomer).map(([name, cJobs]) => ({
          name,
          totalRevenue: cJobs.reduce((sum: number, j: any) => sum + Number(j.job_revenue || 0), 0),
          jobs: cJobs,
        }));
    const headers = ["Customer", "Job #", "Well / Rig", "Date", "Status", "Revenue"];
    const dataRows: string[][] = [];
    customerGroups.forEach((group) => {
      group.jobs.forEach((j: any) => {
        dataRows.push([
          group.name, j.job_number || "—",
          [j.well_name, j.rig_name].filter(Boolean).join(" / ") || "—",
          calendarDayString(j.scheduled_date) || "—",
          humanizeLabel(j.status || '') || "—",
          fmtCurrency(j.job_revenue),
        ]);
      });
      dataRows.push(["", "", "", "", `Subtotal — ${group.name}`, fmtCurrency(group.totalRevenue)]);
    });
    const reportTotal = deriveReportSummary(reportResults).total;
    return {
      headers,
      dataRows,
      grandTotalLabel: reportTotal == null ? undefined : "Report Total",
      grandTotalValue: reportTotal == null ? undefined : fmtCurrency(reportTotal),
    };
  }

  // Tickets & Revenue by Rig — grouped by rig
  if (type === "rev-rig" && groups.length > 0) {
    const headers = ["Ticket #", "Customer", "AFE #", "Job Date", "Equipment", "Crew", "Billed"];
    const dataRows: string[][] = [];
    groups.forEach((group: any) => {
      const groupLabel = group.rig_name || group.label || "Unknown Rig";
      (group.lines || []).forEach((r: any) => {
        dataRows.push([
          r.ticket_number || r.ticket || "—",
          r.customer_name || r.customer || "—",
          r.afe_number || r.afe || "—",
          r.job_date || r.work_date || r.date || "—",
          // Same three reads as the on-screen cells, through the same helpers.
          // The export did not crash — a sheet cell coerces an object to
          // "[object Object]" rather than throwing — so the PDF, CSV and Excel
          // of this report shipped that string in Equipment and Crew, and "—"
          // in every Billed cell, while the subtotal line under them was right.
          rigEquipmentLabel(r) ?? "—",
          rigCrewLabel(r) ?? "—",
          fmtCurrency(rigBilled(r)),
        ]);
      });
      if (group.subtotal != null) {
        dataRows.push(["", "", "", "", "", `Subtotal — ${groupLabel}`, fmtCurrency(Number(group.subtotal))]);
      }
    });
    // `deriveReportSummary`, not a fourth reading of `grand_total`: by-rig's
    // object spells it `total_billed`, so `?.total_revenue` was 0 here too.
    const gtNum = deriveReportSummary(reportResults).total ?? 0;
    return {
      headers, dataRows,
      grandTotalLabel: gtNum > 0 ? "Grand Total" : undefined,
      grandTotalValue: gtNum > 0 ? fmtCurrency(gtNum) : undefined,
    };
  }

  // Grouped (generic — other grouped reports)
  if (groups.length > 0) {
    const headers = ["Ticket #", "Date", "Rig / Well", "Service", "Qty", "Rate", "Amount"];
    const dataRows: string[][] = [];
    groups.forEach((group: any) => {
      const groupLabel = group.rig_name || group.label || group.customer || group.itemType;
      (group.lines || []).forEach((r: any) => {
        dataRows.push([
          r.ticket || r.ticket_number || "—",
          r.date || r.work_date || "—",
          r.rig || r.rig_name || r.well_name || "—",
          humanizeLabel(r.service || r.item_type || '') || "—",
          r.qty?.toLocaleString?.() ?? String(r.quantity ?? "—"),
          fmtCurrency(r.rate),
          r.amount != null ? fmtCurrency(Number(r.amount)) : "—",
        ]);
      });
      if (group.subtotal != null) {
        dataRows.push(["", "", "", "", "", `Subtotal — ${groupLabel}`, fmtCurrency(Number(group.subtotal))]);
      }
    });
    // grand_total can be a number or an object with total_revenue
    // `deriveReportSummary`, not a fourth reading of `grand_total`: by-rig's
    // object spells it `total_billed`, so `?.total_revenue` was 0 here too.
    const gtNum = deriveReportSummary(reportResults).total ?? 0;
    return {
      headers, dataRows,
      grandTotalLabel: gtNum > 0 ? "Grand Total" : undefined,
      grandTotalValue: gtNum > 0 ? fmtCurrency(gtNum) : undefined,
    };
  }

  // Flat rows (by-rig, ops, payroll, safety)
  if (rows.length > 0) {
    const visibleCols = Object.keys(rows[0]).filter((k) => !isHiddenColumn(k));
    const headers = visibleCols.map(formatColumnHeader);
    const dataRows = rows.map((r: any) => visibleCols.map((k) => formatCellValue(k, r[k], timeZone)));
    return { headers, dataRows };
  }

  return { headers: [], dataRows: [] };
}

function makeFilename(reportName: string, dateFrom: string, dateTo: string): string {
  return `${reportName.replace(/[^a-zA-Z0-9]+/g, "_")}_${dateFrom}_to_${dateTo}`;
}

/* ─── Register pieces ─── */

/**
 * A group header is NOT a record. One cell spanning every column, carrying the
 * same 4px rail as the heading row in no colour at all — a coloured rail here
 * would read as a status for the whole group. No tabIndex, no click, no
 * selection tint.
 */
function GroupHeader({ label, count }: { label: string; count: number }) {
  return (
    <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent bg-fieldiq-dark/40">
      {/* One cell spanning every column: 3 now the "Opens" column is gone. */}
      <td colSpan={3} className="px-4 py-2.5">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h3 className="text-xs font-black uppercase tracking-[0.1em] text-white">{label}</h3>
          {/* A bare number, not "(3)" — `getByText('3')` is then an exact
              match on this element's own text, and the separator is a
              sibling rather than a wrapper. */}
          <span aria-hidden="true" className="text-fieldiq-muted">·</span>
          <span className="text-xs tabular-nums text-fieldiq-muted">{count}</span>
        </div>
      </td>
    </tr>
  );
}

/**
 * The ROW is the link. There is no Run button beside it — a clickable row with
 * a button in it is the fe#647 trap, and the button added nothing the row did
 * not already do.
 *
 * A row the caller's role cannot run does not navigate at all: it says who can,
 * and stays put. Letting it through to a 403 spends a page load to deliver the
 * same refusal with less information.
 */
function CatalogRow({
  report,
  verdict,
  onOpen,
}: {
  report: ReportTemplate;
  verdict: GateVerdict;
  onOpen: () => void;
}) {
  const refused = verdict === "refused";
  const Icon = report.icon;

  const keys = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <tr
      data-testid="report-row"
      data-report-id={report.id}
      {...(refused
        ? { "aria-disabled": true }
        : { tabIndex: 0, onClick: onOpen, onKeyDown: keys })}
      className={`border-t border-l-4 border-fieldiq-border outline-none transition ${
        refused
          ? "border-l-transparent"
          : "cursor-pointer border-l-transparent hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10"
      }`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-fi-amber/15">
            <Icon className="size-3.5 text-fieldiq-accent-signal" aria-hidden="true" />
          </span>
          <span className={`text-xs font-bold ${refused ? "text-fieldiq-muted" : "text-fieldiq-text"}`}>
            {report.name}
          </span>
          {refused && (
            <span className="inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink">
              <Lock className="size-3" aria-hidden="true" />
              {GATE_ALLOWED_LABEL[report.gate]}
            </span>
          )}
        </div>
      </td>
      <td className="max-w-[420px] px-4 py-3 text-xs text-fieldiq-muted">{report.description}</td>
      <td className="px-4 py-3 text-right">
        {refused ? null : <ChevronRight className="ml-auto size-4 text-fieldiq-muted" aria-hidden="true" />}
      </td>
    </tr>
  );
}

/** The same row below md. Cards carry no rail. */
function CatalogCard({
  report,
  verdict,
  onOpen,
}: {
  report: ReportTemplate;
  verdict: GateVerdict;
  onOpen: () => void;
}) {
  const refused = verdict === "refused";
  const Icon = report.icon;
  return (
    <button
      type="button"
      data-testid="report-card"
      data-report-id={report.id}
      disabled={refused}
      onClick={onOpen}
      className="flex w-full items-start gap-2.5 px-4 py-3 text-left transition disabled:cursor-default enabled:hover:bg-fieldiq-dark/50"
    >
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-fi-amber/15">
        <Icon className="size-3.5 text-fieldiq-accent-signal" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-xs font-bold ${refused ? "text-fieldiq-muted" : "text-fieldiq-text"}`}>
          {report.name}
        </span>
        <span className="mt-0.5 block text-[0.7rem] text-fieldiq-muted">{report.description}</span>
        {refused && (
          <span className="mt-1.5 inline-flex w-fit items-center gap-1.5 rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink">
            <Lock className="size-3" aria-hidden="true" />
            {GATE_ALLOWED_LABEL[report.gate]}
          </span>
        )}
      </span>
    </button>
  );
}

/* ─── Component ─── */

const UnifiedReports = () => {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { hasVertical, user } = useAuth();
  const timeZone = useTenantTimeZone();

  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // The open report and its date range live in the URL — `?report=<id>&from=
  // &to=` — not in component state. A row on a report opens its own page
  // (an employee on Hours & Jobs), and that page's back arrow has to land on
  // the same report over the same dates; state held here is gone the moment
  // this screen unmounts. A link or a refresh now keeps them too.
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const reportParam = searchParams.get("report");
  const dateFrom = searchParams.get("from") || getThisMonthRange(timeZone)[0];
  const dateTo = searchParams.get("to") || getThisMonthRange(timeZone)[1];

  // Sort state (shared across all report tables; resets on report switch)
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = makeToggleSort(setSortBy, setSortDir, sortBy);

  // Print ref
  const printRef = useRef<HTMLDivElement>(null);

  /* ─── Filtered report templates ─── */
  // Vertical gating happens here rather than in the module-scope constant so a
  // tenant that enables a vertical mid-session sees its rows without a reload.
  const availableTemplates = useMemo(
    () =>
      visibleTemplates.filter(
        (t) => !t.verticals || t.verticals.some((v) => hasVertical(v)),
      ),
    [hasVertical],
  );

  // An id this tenant cannot see (another vertical's row, a stale link) is the
  // catalog, not a guessed report.
  const selectedReport = useMemo(
    () => (reportParam && availableTemplates.find((t) => t.id === reportParam && !t.route)) || null,
    [reportParam, availableTemplates],
  );
  // Opening a report runs it; there is no "open but not run" state.
  const shouldFetch = !!selectedReport;

  const filteredReports = useMemo(() => {
    return availableTemplates.filter((r) => {
      if (categoryFilter !== "all" && r.category !== categoryFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!r.name.toLowerCase().includes(q) && !r.description.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [availableTemplates, categoryFilter, searchQuery]);

  const groupedReports = useMemo(() => {
    const groups: Record<string, ReportTemplate[]> = {};
    filteredReports.forEach((r) => {
      if (!groups[r.category]) groups[r.category] = [];
      groups[r.category].push(r);
    });
    return groups;
  }, [filteredReports]);

  /* ─── Access, and the four band figures ─── */
  // Mirrored from the API's gates; see ReportGate and gateVerdict above.
  const verdictOf = useCallback(
    (t: ReportTemplate): GateVerdict => gateVerdict(t.gate, user?.role),
    [user?.role],
  );

  /**
   * Every figure on the band comes off `availableTemplates` — the rows this
   * tenant and role can actually see. No query is made for any of them, so
   * none of them can be in flight and none needs a loading pulse.
   *
   * There is no fifth. "Service lines" was drafted and cut: the verticals this
   * tenant has enabled (Smith: casing_running + midstream) contribute no
   * vertical-gated rows at all, so the figure would have been a true fact
   * about the tenant sitting on a page where it means nothing.
   */
  const bandStats = useMemo((): PageHeaderStat[] => {
    const refused = availableTemplates.filter((t) => verdictOf(t) === "refused").length;
    const ownPage = availableTemplates.filter((t) => !!t.route).length;
    return [
      {
        icon: <LibraryBig className="size-4" />,
        value: displayValue(availableTemplates.length),
        label: "Reports",
        sub: "In this catalog",
        tone: "accent",
      },
      {
        icon: <LayoutList className="size-4" />,
        value: displayValue(availableTemplates.length - ownPage),
        label: "Run here",
        sub: "Pick a date range",
      },
      {
        // THIRD SLOT — the one the hero circle's edge crosses between 1024 and
        // 1048 wide, where the label has a ~70px ceiling (fe#698). "Linked"
        // renders 49.8px at 1032, inside the 35–71px band the four pages
        // measured before Equipment occupy. It is the short spelling on
        // purpose: "Own page" measured 71.1px — clear on the sweep, but 0.1px
        // past the top of the measured range, and the sub-line below carries
        // the meaning anyway.
        icon: <ExternalLink className="size-4" />,
        value: displayValue(ownPage),
        label: "Linked",
        sub: "Opens its own screen",
      },
      {
        icon: <Lock className="size-4" />,
        value: displayValue(refused),
        label: "Restricted",
        // 18 characters. fe#712 found a 26-character sub-line crossing the
        // circle in this slot.
        sub: "Needs other roles",
        tone: refused > 0 ? "info" : "neutral",
      },
    ];
  }, [availableTemplates, verdictOf]);

  /* ─── API Query ─── */
  /**
   * `LOAD_FAILURE_QUERY_OPTIONS` replaces the `retry: false` this query used to
   * carry. The two are not the same policy and the difference is the point: the
   * old one declined to retry a dropped connection as well as a refusal, and —
   * more importantly — left `networkMode` at React Query's default `'online'`,
   * where a failure that WANTS to retry parks in `paused` with `error: null`.
   * Every branch below keys on the failure, so a paused query rendered the
   * runner's reassuring "no data for this period" over a 500.
   *
   * `queryLoadFailure` reads the failure off the query rather than off `error`
   * alone, so a paused attempt still counts as a load that has not arrived.
   */
  const reportQuery = useQuery({
    queryKey: ["report-run", selectedReport?.id, dateFrom, dateTo],
    queryFn: async (): Promise<Record<string, any> | null> => {
      if (!selectedReport) return null;
      try {
        const params: Record<string, string> = { dateFrom, dateTo };
        const res = await apiClient.get(selectedReport.endpoint, { params });
        const parsed = parseReportResponse(selectedReport.id, res.data);
        return { type: selectedReport.id, ...parsed };
      } catch (err: any) {
        console.error(`[Report:${selectedReport.id}] Fetch failed:`, err);
        if (err?.response?.data) {
          console.error(`[Report:${selectedReport.id}] Raw response body:`, JSON.stringify(err.response.data, null, 2));
        }
        throw err;
      }
    },
    enabled: shouldFetch && !!selectedReport,
    refetchOnWindowFocus: false,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const {
    data: reportResults,
    isLoading: isRunning,
    isFetching,
  } = reportQuery;
  const reportFailure = queryLoadFailure(reportQuery);

  /* ─── Handlers ─── */
  /** Writes the runner's URL. A date change replaces; opening a report pushes. */
  const writeParams = useCallback(
    (patch: Record<string, string | null>, opts?: { push?: boolean }) => {
      const next = new URLSearchParams(searchParams);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null) next.delete(k);
        else next.set(k, v);
      }
      setSearchParams(next, opts?.push ? { state: { fromCatalog: true } } : { replace: true, state: location.state });
    },
    [searchParams, setSearchParams, location.state],
  );
  const setDateFrom = useCallback((v: string) => writeParams({ from: v }), [writeParams]);
  const setDateTo = useCallback((v: string) => writeParams({ to: v }), [writeParams]);

  const handleRunReport = useCallback((report: ReportTemplate) => {
    // The dates go into the URL with the report, so the page it opens and a
    // copied link both carry the range the reader was looking at.
    // Before the tenant's zone arrives the default range is empty; writing
    // that would pin a blank range into the URL, so it waits for the default.
    writeParams({ report: report.id, from: dateFrom || null, to: dateTo || null }, { push: true });
    setSortBy('');
    setSortDir('asc');
  }, [writeParams, dateFrom, dateTo]);

  /**
   * Where a catalog row goes. Unchanged from the Run/View button it replaces:
   * a row with a `route` navigates to its own screen, a row with an `endpoint`
   * opens the inline runner. The row is the target now, so this is the single
   * place that decision is made.
   */
  const openReport = useCallback(
    (report: ReportTemplate) => {
      if (report.route) navigate(report.route);
      else handleRunReport(report);
    },
    [navigate, handleRunReport],
  );

  // Opened from the catalog, the entry behind this one IS the catalog, so
  // step back to it rather than stacking a second copy. Opened cold (a link,
  // a refresh, the back arrow of an employee page), drop the report from the
  // URL in place. The dates stay either way.
  const handleBackToList = useCallback(() => {
    if ((location.state as { fromCatalog?: boolean } | null)?.fromCatalog) navigate(-1);
    else writeParams({ report: null });
  }, [location.state, navigate, writeParams]);

  // Changing the date range auto-runs the report: the query key includes
  // dateFrom/dateTo, which are read from the URL.
  const applyQuickRange = useCallback((range: [string, string]) => {
    writeParams({ from: range[0], to: range[1] });
  }, [writeParams]);

  // `handleExportCsv` — a `toast("CSV export coming soon")` stub wired to an
  // "Export CSV" button on the runner's old header — is gone. It sat about 190
  // lines above a WORKING CSV button (`handleExportCSVDetail`, which writes a
  // real sheet through XLSX), so the runner offered the reader two identical
  // affordances of which the more prominent one did nothing. Same removal
  // fe#715 made to the catalog's copy of the same dead button.

  /* ─── Helpers ─── */
  // fmtCurrency is imported from src/lib/display-labels — it used to be redefined
  // here as `(n: number) => \`$${n.toLocaleString(...)}\``, which rendered an
  // absent amount as "$0.00" (and threw on undefined, since the rows are `any`).

  // Sort helper — returns a sorted copy; no-op when sortBy is empty
  const applySortTo = (arr: any[], accessor?: (row: any, field: string) => any): any[] => {
    if (!sortBy || arr.length === 0) return arr;
    const get = accessor || ((row: any, f: string) => row[f]);
    return [...arr].sort((a, b) => sortCompare(get(a, sortBy), get(b, sortBy), sortDir));
  };

  // Compact SortTh overrides for report tables
  const sc = "!text-[10px] !tracking-wide !py-1 !px-2";
  const scR = sc + " !text-right";
  const scHide = sc + " hidden sm:table-cell";

  const rows: any[] = reportResults?.rows || reportResults?.data || [];
  const groups: any[] = reportResults?.groups || [];
  const byDateTickets: any[] = reportResults?.tickets || [];
  const byDateGrandTotal: Record<string, any> = reportResults?.dateGrandTotal || {};

  /**
   * The three register-level figures, derived ONCE for this result and read by
   * both the band and the body. See deriveReportSummary — the band used to
   * count different keys from the ones the parse branches produce, and
   * disagreed with the table under it on four of the six runnable reports.
   */
  const summary = useMemo(() => deriveReportSummary(reportResults), [reportResults]);

  /**
   * The coalesced number for the body's `> 0` guards only. The band must use
   * `summary.total` directly: this one turns an absent total into 0, and 0 is
   * the one thing a money slot must never say when it means "none was sent".
   */
  const grandTotalNum: number = summary.total ?? 0;

  /**
   * THE RUNNER'S FOUR BAND FIGURES, ALL OFF THE RESULT ALREADY IN HAND.
   *
   * No query is made for any of them — they are counts of the payload this
   * screen has already parsed, plus the date range held in this component's
   * own state. `loading` carries the in-flight case, so none of them renders a
   * placeholder zero while the report runs.
   *
   * SLOT 2 AND SLOT 3 ARE EM DASHES ON THE REPORTS THAT HAVE NO SUCH FIGURE,
   * AND THAT IS THE HONEST ANSWER RATHER THAN A ZERO. Equipment Utilization
   * returns no grand total and is not grouped; "$0.00" and "0 groups" would
   * both be claims the payload never made. So these read `summary.total` and
   * `summary.groups`, which are `null` when absent, never the coalesced
   * `grandTotalNum` — coalescing renders every total-less report as $0.00,
   * which is the silent-blank-field bug in its money spelling.
   *
   * AND AN EM DASH HERE MUST MEAN THE BODY HAS NOTHING EITHER. Every figure
   * comes from `deriveReportSummary`, the same derivation the body's
   * grand-total lines read, so the band cannot claim "—" over a table showing
   * a total — which is exactly what it did on four of six reports before
   * fe#725. That docblock has the measurements.
   *
   * Slot 3's label is "Groups", 47.6px at 1032 — inside the 35–71px band the
   * pages measured before Equipment occupy (fe#698). Slot 4's sub-line is 15
   * characters; fe#712 found a 26-character one crossing the circle there.
   */
  const runnerStats = useMemo((): PageHeaderStat[] => {
    const inFlight = isRunning || isFetching;

    // A span in days, inclusive of both ends. Built from the calendar-day
    // strings with local construction — `new Date('2026-03-23')` is UTC
    // midnight and lands on the 22nd west of Greenwich.
    const spanDays = (() => {
      const toLocal = (s: string) => parseCalendarDate(String(s));
      const a = toLocal(dateFrom);
      const b = toLocal(dateTo);
      if (!a || !b) return null;
      return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
    })();

    return [
      {
        icon: <Rows3 className="size-4" />,
        value: reportFailure ? "—" : displayValue(summary.records),
        label: "Records",
        sub: "In this result",
        tone: "accent",
        loading: inFlight,
      },
      {
        icon: <DollarSign className="size-4" />,
        // `summary.total`, not the coalesced `grandTotalNum`: a report that
        // sends no total reads "—", never "$0.00".
        value: reportFailure ? "—" : fmtCurrency(summary.total),
        label: "Total",
        sub: "Report grand total",
        loading: inFlight,
      },
      {
        icon: <Layers className="size-4" />,
        value:
          reportFailure || summary.groups == null
            ? "—"
            : displayValue(summary.groups),
        label: "Groups",
        sub: "Subtotalled sections",
        loading: inFlight,
      },
      {
        icon: <CalendarRange className="size-4" />,
        value: spanDays == null ? "—" : `${spanDays}`,
        label: "Days",
        // 15 characters.
        sub: "Range covered",
      },
    ];
  }, [summary, dateFrom, dateTo, isRunning, isFetching, reportFailure]);

  /* ─── Export handlers ─── */
  const exportData = useMemo(
    () => buildExportData(reportResults, timeZone),
    [reportResults, timeZone],
  );

  const handleExportCSVDetail = useCallback(() => {
    const { headers, dataRows, grandTotalLabel, grandTotalValue } = exportData;
    if (headers.length === 0) { toast.error("No data to export"); return; }
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    if (grandTotalLabel && grandTotalValue) {
      XLSX.utils.sheet_add_aoa(ws, [[grandTotalLabel, "", "", "", "", grandTotalValue]], { origin: -1 });
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    const filename = makeFilename(selectedReport?.name || "Report", dateFrom, dateTo);
    XLSX.writeFile(wb, `${filename}.csv`, { bookType: "csv" });
    toast.success("CSV downloaded");
  }, [exportData, selectedReport, dateFrom, dateTo]);

  const handleExportExcel = useCallback(() => {
    const { headers, dataRows, grandTotalLabel, grandTotalValue } = exportData;
    if (headers.length === 0) { toast.error("No data to export"); return; }
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    if (grandTotalLabel && grandTotalValue) {
      XLSX.utils.sheet_add_aoa(ws, [[grandTotalLabel, "", "", "", "", grandTotalValue]], { origin: -1 });
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    const filename = makeFilename(selectedReport?.name || "Report", dateFrom, dateTo);
    XLSX.writeFile(wb, `${filename}.xlsx`, { bookType: "xlsx" });
    toast.success("Excel downloaded");
  }, [exportData, selectedReport, dateFrom, dateTo]);

  const handleExportPDF = useCallback(() => {
    const { headers, dataRows, grandTotalLabel, grandTotalValue } = exportData;
    if (headers.length === 0) { toast.error("No data to export"); return; }
    const doc = new jsPDF({ orientation: "landscape" });
    const title = selectedReport?.name || "Report";
    doc.setFontSize(16);
    doc.text(title, 14, 18);
    doc.setFontSize(10);
    doc.text(`${dateFrom} to ${dateTo}`, 14, 26);

    const bodyRows = [...dataRows];
    if (grandTotalLabel && grandTotalValue) {
      const totalRow = new Array(headers.length).fill("");
      totalRow[0] = grandTotalLabel;
      totalRow[totalRow.length - 1] = grandTotalValue;
      bodyRows.push(totalRow);
    }

    autoTable(doc, {
      startY: 32,
      head: [headers],
      body: bodyRows,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [30, 41, 59], textColor: 255 },
    });

    const filename = makeFilename(title, dateFrom, dateTo);
    doc.save(`${filename}.pdf`);
    toast.success("PDF downloaded");
  }, [exportData, selectedReport, dateFrom, dateTo]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: makeFilename(selectedReport?.name || "Report", dateFrom, dateTo),
  });

  return (
    <div data-testid="reports-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global <Header />. Pages never do. */}
      {!selectedReport && (
        <PageHeaderBand
          eyebrow="Business intelligence"
          title="Reports"
          onBack={() => navigate(-1)}
          backLabel="Back"
          stats={bandStats}
        />
      )}

      {/* THE RUNNER, on the same band as the catalog.
          The title is the REPORT's name, not "Reports" — the deprecated header
          this replaces said "Reports" on every one of them, so the page gave
          no clue which report you had opened once you had scrolled past the
          sub-header. The eyebrow carries the catalog category the row came
          from, which is the same word the chip you arrived through was
          spelling.

          The header's dead "Export CSV" button is gone; see handleExportCsv's
          removal. The working PDF / CSV / Excel / Print bar is in the body
          with the date controls it belongs beside. */}
      {selectedReport && (
        <PageHeaderBand
          eyebrow={categoryLabels[selectedReport.category] || "Report"}
          title={selectedReport.name}
          // `handleBackToList`, NOT a bare `navigate(-1)`: a report opened
          // cold has no catalog behind it, and history-back would drop the
          // reader on whatever page they were on before Reports.
          onBack={handleBackToList}
          backLabel="Back to the report catalog"
          stats={runnerStats}
        />
      )}

      <div>
        {/* ─── LIST VIEW (the catalog) ─── */}
        {!selectedReport && (
          <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
            <div className="mx-auto max-w-[1920px] space-y-4">
              {/* Filter chips + search. Chips, not tabs: every chip narrows the
                  SAME list of catalog rows and asks the same question of them
                  (fe#704's test). They carry counts because the catalog is a
                  static registry this screen holds in full — the figure is
                  exact and no endpoint is involved. */}
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
                  {CATEGORY_FILTERS.map((cat) => {
                    const active = categoryFilter === cat;
                    const count = cat === "all"
                      ? availableTemplates.length
                      : availableTemplates.filter((r) => r.category === cat).length;
                    return (
                      <button
                        key={cat}
                        type="button"
                        data-testid="category-chip"
                        data-category={cat}
                        onClick={() => setCategoryFilter(cat)}
                        className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                          active
                            ? "bg-fi-amber text-fi-amber-ink"
                            : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
                        }`}
                      >
                        {cat === "all" ? "All Reports" : categoryLabels[cat]}
                        <span className={`tabular-nums ${active ? "text-fi-amber-ink" : "text-fieldiq-muted"}`}>
                          {displayValue(count)}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="relative lg:w-72">
                  <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-fieldiq-muted" />
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search reports..."
                    aria-label="Search reports"
                    className="h-9 w-full rounded-fi-pill border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-xs text-fieldiq-text placeholder-fieldiq-muted focus:outline-none focus:ring-1 focus:ring-fi-amber"
                  />
                </div>
              </div>

              {/* The register — ONE panel over every group, not a panel each. */}
              <section
                aria-labelledby="reports-register-title"
                className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
              >
                <header className="border-b border-fieldiq-border px-4 py-4">
                  <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                    Report catalog
                  </p>
                  <h2 id="reports-register-title" className="mt-1 text-base font-extrabold tracking-tight text-white">
                    What this tenant can pull
                  </h2>
                </header>

                {filteredReports.length === 0 ? (
                  /* Loaded, and genuinely empty — worded as a fact about the
                     data. There is no failure branch here on purpose: the
                     catalog is a module constant, so it cannot fail to load
                     and cannot be refused. The three-state rule applies to a
                     LIST THAT CAME OVER THE WIRE; claiming a failure state
                     this page can never enter would be theatre. */
                  <div className="px-4 py-12 text-center">
                    <Search className="mx-auto mb-3 size-10 text-fieldiq-muted" aria-hidden="true" />
                    <p className="text-sm text-fieldiq-text">No report matches this filter.</p>
                    <p className="mt-1 text-xs text-fieldiq-muted">
                      Clear the search, or pick another category.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="hidden overflow-x-auto md:block">
                      <table className="w-full min-w-[720px] text-left">
                        <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                          {/* The heading row carries the same 4px in no colour. */}
                          <tr className="border-l-4 border-l-transparent">
                            {/* No "Opens" column. Its two values described
                                where the row rendered — inline here, or on its
                                own screen — which is a fact about this
                                component's own dispatch, not about the report.
                                Whether the row renders inline or routes is
                                something the reader finds out by clicking, and
                                nothing they can act on beforehand. The access
                                badge that shared the cell has moved beside the
                                report's name, where it qualifies the thing it
                                is about. */}
                            <th scope="col" className="px-4 py-3">Report</th>
                            <th scope="col" className="px-4 py-3">What it covers</th>
                            <th scope="col" className="w-[52px] px-4 py-3" />
                          </tr>
                        </thead>
                        {CATEGORY_ORDER.filter((c) => groupedReports[c]?.length).map((category) => (
                          <tbody key={category} role="region" aria-label={categoryLabels[category] || category}>
                            <GroupHeader
                              label={categoryLabels[category] || category}
                              count={groupedReports[category].length}
                            />
                            {groupedReports[category].map((report) => (
                              <CatalogRow
                                key={report.id}
                                report={report}
                                verdict={verdictOf(report)}
                                onOpen={() => openReport(report)}
                              />
                            ))}
                          </tbody>
                        ))}
                      </table>
                    </div>

                    {/* Below md the same rows stack as cards, and carry no rail. */}
                    <div className="divide-y divide-fieldiq-border md:hidden">
                      {CATEGORY_ORDER.filter((c) => groupedReports[c]?.length).map((category) => (
                        <div key={category}>
                          <div className="flex items-baseline gap-2 bg-fieldiq-dark/40 px-4 py-2.5">
                            <h3 className="text-xs font-black uppercase tracking-[0.1em] text-white">
                              {categoryLabels[category] || category}
                            </h3>
                            <span aria-hidden="true" className="text-fieldiq-muted">·</span>
                            <span className="text-xs tabular-nums text-fieldiq-muted">
                              {groupedReports[category].length}
                            </span>
                          </div>
                          {groupedReports[category].map((report) => (
                            <CatalogCard
                              key={report.id}
                              report={report}
                              verdict={verdictOf(report)}
                              onOpen={() => openReport(report)}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* A scope line: what you are looking at, out of what this
                    tenant HAS. The denominator used to be `visibleTemplates`,
                    the ungated registry — so a casing tenant read "Showing 7 of
                    13" and six of those thirteen were rows it can never see.
                    A scope that counts rows the reader cannot reach is not a
                    scope, it is a count of somebody else's catalog. */}
                <p
                  data-testid="reports-scope"
                  className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted"
                >
                  Showing {filteredReports.length} of {availableTemplates.length} reports available to this account.
                </p>
              </section>
            </div>
          </section>
        )}

        {/* ─── DETAIL VIEW (inline report runner) ─── */}
        {/* The same body shell the catalog uses: page padding from the `--fi-*`
            page tokens, content capped and centred, bottom padding clearing the
            mobile bar. It replaces a bare `px-6 py-4`, which set neither the
            cap nor the bottom clearance. Every control below — the export bar,
            the date inputs, the quick-range pills — sits HERE, in the body,
            never on the band. */}
        {selectedReport && (
          <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
            <div className="mx-auto max-w-[1920px] space-y-4">
            {/* Detail sub-header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleBackToList}
                  className="flex items-center gap-1.5 text-fieldiq-muted hover:text-fieldiq-text text-xs font-semibold transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  All Reports
                </button>
                <div className="w-px h-5 bg-fieldiq-border" />
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-fieldiq-accent/15 flex items-center justify-center shrink-0">
                    <selectedReport.icon className="w-3.5 h-3.5 text-fieldiq-accent" />
                  </div>
                  <span className="text-sm font-bold text-foreground">{selectedReport.name}</span>
                </div>
              </div>
              {/* Export / Print bar */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleExportPDF}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-fieldiq-border bg-fieldiq-card text-[11px] font-semibold text-white hover:bg-fieldiq-border/50 transition-colors"
                >
                  <FileText className="w-3 h-3" /> PDF
                </button>
                <button
                  onClick={handleExportCSVDetail}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-fieldiq-border bg-fieldiq-card text-[11px] font-semibold text-white hover:bg-fieldiq-border/50 transition-colors"
                >
                  <Download className="w-3 h-3" /> CSV
                </button>
                <button
                  onClick={handleExportExcel}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-fieldiq-border bg-fieldiq-card text-[11px] font-semibold text-white hover:bg-fieldiq-border/50 transition-colors"
                >
                  <Download className="w-3 h-3" /> Excel
                </button>
                <button
                  onClick={() => handlePrint()}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-fieldiq-border bg-fieldiq-card text-[11px] font-semibold text-white hover:bg-fieldiq-border/50 transition-colors"
                >
                  <Printer className="w-3 h-3" /> Print
                </button>
              </div>
            </div>

            {/* Date controls */}
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-[11px] font-medium text-fieldiq-muted mb-1.5">Start Date</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9 rounded-lg bg-fieldiq-card border border-fieldiq-border px-3 text-xs text-white focus:outline-none focus:ring-1 focus:ring-fieldiq-accent"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-fieldiq-muted mb-1.5">End Date</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9 rounded-lg bg-fieldiq-card border border-fieldiq-border px-3 text-xs text-white focus:outline-none focus:ring-1 focus:ring-fieldiq-accent"
                />
              </div>
              {/* Quick range pills */}
              {[
                { label: "This Week", range: getThisWeekRange },
                { label: "This Month", range: getThisMonthRange },
                { label: "YTD", range: getYTDRange },
              ].map((q) => (
                <button
                  key={q.label}
                  onClick={() => applyQuickRange(q.range(timeZone))}
                  className="h-9 px-3 rounded-lg bg-fieldiq-card border border-fieldiq-border text-[11px] font-semibold text-fieldiq-muted hover:text-fieldiq-text transition-colors"
                >
                  {q.label}
                </button>
              ))}
            </div>

            {/* Loading state */}
            {(isRunning || isFetching) && (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-fieldiq-accent/30 border-t-fieldiq-accent rounded-full animate-spin" />
                  <span className="text-xs text-fieldiq-muted font-medium">Running report...</span>
                </div>
              </div>
            )}

            {/* AN ERROR IS NOT AN EMPTY STATE, AND A REFUSAL IS NOT "NO DATA".
                api#741 gates these endpoints into four groups, so a 403 here is
                an ordinary outcome for a supervisor on a pay report — and the
                branch this replaces answered every failure the same way, in a
                sentence aimed at a developer ("check the browser console",
                "may have returned invalid JSON") over a RED SUCCESS CHECKMARK.
                `CheckCircle2` was the glyph on all four of this runner's
                states. LoadFailureNotice splits refused from unreachable and
                says which, with a lock or a warning triangle accordingly, and
                states out loud that the result is not a fact about the data. */}
            {shouldFetch && !isRunning && !isFetching && reportFailure && (
              <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
                <LoadFailureNotice
                  error={queryFailureError(reportQuery)}
                  kind={reportFailure}
                  subject={`the ${selectedReport.name.toLowerCase()} report`}
                />
              </div>
            )}

            {/* Results */}
            {shouldFetch && reportResults && !isRunning && !isFetching && (
              <div ref={printRef} className="report-print-area space-y-4">
                {/* Force dark-on-white when printing — text is white in dark
                    mode, so without this the printout would be white-on-white. */}
                <style>{`
                  @media print {
                    .report-print-area,
                    .report-print-area * {
                      color: #0f172a !important;
                      background: #ffffff !important;
                      border-color: #cbd5e1 !important;
                    }
                  }
                `}</style>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-fieldiq-accent" />
                  <span className="text-xs font-semibold text-foreground">Results</span>
                  <span className="text-[10px] text-foreground ml-auto">
                    {dateFrom} to {dateTo}
                  </span>
                </div>

                {/* Tickets & Revenue by Rig — grouped by rig */}
                {groups.length > 0 && reportResults?.type === "rev-rig" && (
                  <div className="space-y-4">
                    {groups.map((group: any, gi: number) => {
                      const groupLabel = group.rig_name || group.label || `Rig ${gi + 1}`;
                      const sortedLines = applySortTo(group.lines || [], (r, f) => {
                        switch (f) {
                          case 'ticket': return r.ticket_number || r.ticket;
                          case 'customer': return r.customer_name || r.customer;
                          case 'afe': return r.afe_number || r.afe || '';
                          case 'job_date': return r.job_date || r.work_date || r.date;
                          case 'equipment': return rigEquipmentLabel(r) ?? '';
                          case 'crew': return rigCrewLabel(r) ?? '';
                          case 'billed': return rigBilled(r) ?? 0;
                          default: return r[f];
                        }
                      });
                      return (
                      <div key={groupLabel + gi} className="space-y-1">
                        <div className="flex items-center justify-between px-1">
                          <h4 className="text-[11px] font-bold text-foreground uppercase tracking-wide">
                            {groupLabel}
                          </h4>
                          {group.lines?.length != null && (
                            <span className="text-[10px] text-foreground">
                              {group.lines.length} ticket{group.lines.length !== 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                        <div className="rounded-lg border border-fieldiq-border overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-fieldiq-card/50">
                                <SortTh label="Ticket #" field="ticket" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                                <SortTh label="Customer" field="customer" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                                <SortTh label="AFE #" field="afe" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scHide} />
                                <SortTh label="Job Date" field="job_date" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                                <SortTh label="Equipment" field="equipment" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scHide} />
                                <SortTh label="Crew" field="crew" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scHide} />
                                <SortTh label="Billed" field="billed" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {sortedLines.map((r: any, idx: number) => (
                                <TableRow key={`${r.ticket_number || r.ticket || idx}`} className="hover:bg-fieldiq-card/20 border-fieldiq-border">
                                  <TableCell className="px-2 py-1.5 text-[11px] font-medium text-fieldiq-accent">{r.ticket_number || r.ticket || "—"}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] text-foreground">{r.customer_name || r.customer || "—"}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] text-foreground hidden sm:table-cell">{r.afe_number || r.afe || "—"}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[10px] text-foreground">{r.job_date || r.work_date || r.date || "—"}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] text-foreground hidden sm:table-cell">{rigEquipmentLabel(r) ?? "—"}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] text-foreground hidden sm:table-cell">{rigCrewLabel(r) ?? "—"}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] font-semibold text-foreground text-right">{fmtCurrency(rigBilled(r))}</TableCell>
                                </TableRow>
                              ))}
                              {group.subtotal != null && (
                                <TableRow className="bg-fieldiq-card/30 border-t-2 border-fieldiq-border">
                                  <TableCell className="px-2 py-2 text-[11px] font-bold text-foreground" colSpan={6}>
                                    Subtotal — {groupLabel}
                                  </TableCell>
                                  <TableCell className="px-2 py-2 text-[11px] font-bold text-foreground text-right">
                                    {fmtCurrency(Number(group.subtotal))}
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                      );
                    })}
                    {grandTotalNum > 0 && (
                      <div className="rounded-lg border-2 border-fieldiq-accent/30 bg-fieldiq-accent/5 px-4 py-3 flex items-center justify-between">
                        <span className="text-xs font-bold text-foreground uppercase tracking-wide">Grand Total</span>
                        <span className="text-sm font-bold text-fieldiq-accent">{fmtCurrency(grandTotalNum)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Grouped results (generic grouped) */}
                {groups.length > 0 && reportResults?.type !== "rev-rig" && (
                  <div className="space-y-4">
                    {groups.map((group: any, gi: number) => {
                      const groupLabel = group.rig_name || group.label || group.customer || humanizeLabel(group.itemType || '') || `Group ${gi + 1}`;
                      const sortedLines = applySortTo(group.lines || [], (r, f) => {
                        switch (f) {
                          case 'ticket': return r.ticket || r.ticket_number;
                          case 'date': return r.date || r.work_date;
                          case 'rig': return r.rig || r.rig_name || r.well_name;
                          case 'service': return r.service || r.item_type;
                          case 'qty': return Number(r.qty ?? r.quantity ?? 0);
                          case 'rate': return Number(r.rate ?? 0);
                          case 'amount': return Number(r.amount ?? 0);
                          default: return r[f];
                        }
                      });
                      return (
                      <div key={groupLabel + gi} className="space-y-1">
                        <div className="flex items-center justify-between px-1">
                          <h4 className="text-[11px] font-bold text-foreground uppercase tracking-wide">
                            {groupLabel}
                          </h4>
                          {group.lines?.length != null && (
                            <span className="text-[10px] text-foreground">
                              {group.lines.length} line{group.lines.length !== 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                        <div className="rounded-lg border border-fieldiq-border overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-fieldiq-card/50">
                                <SortTh label="Ticket #" field="ticket" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                                <SortTh label="Date" field="date" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                                <SortTh label="Rig / Well" field="rig" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scHide} />
                                <SortTh label="Service" field="service" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scHide} />
                                <SortTh label="Qty" field="qty" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                                <SortTh label="Rate" field="rate" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                                <SortTh label="Amount" field="amount" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {sortedLines.map((r: any, idx: number) => (
                                <TableRow key={`${r.ticket}-${idx}`} className="hover:bg-fieldiq-card/20 border-fieldiq-border">
                                  <TableCell className="px-2 py-1.5 text-[11px] font-medium text-fieldiq-accent">{r.ticket || r.ticket_number || "—"}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[10px] text-foreground">{r.date || r.work_date || "—"}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] text-foreground hidden sm:table-cell">{r.rig || r.rig_name || r.well_name || "—"}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] text-foreground hidden sm:table-cell">{humanizeLabel(r.service || r.item_type || '') || "—"}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] text-foreground text-right">{r.qty?.toLocaleString?.() ?? r.quantity ?? "—"}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] text-foreground text-right">{fmtCurrency(r.rate)}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] font-semibold text-foreground text-right">{r.amount != null ? fmtCurrency(Number(r.amount)) : "—"}</TableCell>
                                </TableRow>
                              ))}
                              {group.subtotal != null && (
                                <TableRow className="bg-fieldiq-card/30 border-t-2 border-fieldiq-border">
                                  <TableCell className="px-2 py-2 text-[11px] font-bold text-foreground" colSpan={6}>
                                    Subtotal — {groupLabel}
                                  </TableCell>
                                  <TableCell className="px-2 py-2 text-[11px] font-bold text-foreground text-right">
                                    {fmtCurrency(Number(group.subtotal))}
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                      );
                    })}
                    {grandTotalNum > 0 && (
                      <div className="rounded-lg border-2 border-fieldiq-accent/30 bg-fieldiq-accent/5 px-4 py-3 flex items-center justify-between">
                        <span className="text-xs font-bold text-foreground uppercase tracking-wide">Grand Total</span>
                        <span className="text-sm font-bold text-fieldiq-accent">{fmtCurrency(grandTotalNum)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Equipment Utilization Report — dedicated columns (no revenue) */}
                {groups.length === 0 && rows.length > 0 && reportResults?.type === "ops-equipment" && (() => {
                  const sorted = applySortTo(rows);
                  return (
                  <div className="rounded-lg border border-fieldiq-border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-fieldiq-card/50">
                          <SortTh label="Equipment" field="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                          <SortTh label="Type" field="type" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                          <SortTh label="Serial #" field="serial_number" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                          <SortTh label="Jobs" field="jobs_count" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                          <SortTh label="Hours Used" field="hours_used" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                          <SortTh label="Utilization %" field="utilization_rate" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sorted.map((r: any) => (
                          <TableRow key={r.id} className="hover:bg-fieldiq-card/20 border-fieldiq-border">
                            <TableCell className="px-2 py-2 text-[11px] font-medium text-foreground">{r.name}</TableCell>
                            <TableCell className="px-2 py-2 text-[11px] text-foreground">{humanizeLabel(r.type)}</TableCell>
                            <TableCell className="px-2 py-2 text-[11px] text-foreground">{r.serial_number ?? "—"}</TableCell>
                            <TableCell className="px-2 py-2 text-[11px] text-foreground text-right">{r.jobs_count}</TableCell>
                            <TableCell className="px-2 py-2 text-[11px] text-foreground text-right">{Number(r.hours_used).toFixed(1)}</TableCell>
                            <TableCell className="px-2 py-2 text-[11px] text-foreground text-right">{Number(r.utilization_rate).toFixed(1)}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  );
                })()}

                {/* Tickets & Revenue by Date — individual ticket rows + grand total */}
                {reportResults?.type === "rev-date" && byDateTickets.length > 0 && (() => {
                  const gt = byDateGrandTotal;
                  const gtParts: string[] = [];
                  if (gt.revenue_cad) gtParts.push(`CAD ${fmtCurrency(gt.revenue_cad)}`);
                  if (gt.revenue_usd) gtParts.push(`USD ${fmtCurrency(gt.revenue_usd)}`);
                  const gtDisplay = gtParts.length > 0
                    ? gtParts.join("  |  ")
                    : fmtCurrency(gt.total_revenue);
                  const sorted = applySortTo(byDateTickets, (t, f) => {
                    switch (f) {
                      case 'work_date': return t.work_date;
                      case 'ticket_number': return t.ticket_number || t.job_number;
                      case 'ticket_hours': return ticketHoursOf(t) ?? 0;
                      case 'subtotal': return Number(t.subtotal ?? 0);
                      default: return t[f];
                    }
                  });
                  return (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-fieldiq-border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-fieldiq-card/50">
                            <SortTh label="Date" field="work_date" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                            <SortTh label="Ticket #" field="ticket_number" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                            <SortTh label="Customer" field="customer_name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                            <SortTh label="Rig / Well" field="rig_name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scHide} />
                            <SortTh label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                            <SortTh label="Ticket Hrs" field="ticket_hours" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                            <SortTh label="Revenue" field="subtotal" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sorted.map((t: any, idx: number) => (
                            <TableRow key={`${t.ticket_number || t.job_number || idx}`} className="hover:bg-fieldiq-card/20 border-fieldiq-border">
                              <TableCell className="px-2 py-2 text-[10px] text-foreground">{calendarDayString(t.work_date) || "—"}</TableCell>
                              <TableCell className="px-2 py-2 text-[11px] font-medium text-fieldiq-accent">{t.ticket_number || t.job_number || "—"}</TableCell>
                              <TableCell className="px-2 py-2 text-[11px] text-foreground">{t.customer_name || "—"}</TableCell>
                              <TableCell className="px-2 py-2 text-[11px] text-foreground hidden sm:table-cell">{t.rig_name || "—"}</TableCell>
                              <TableCell className="px-2 py-2 text-[10px] text-foreground capitalize">{humanizeLabel(t.status || '') || "—"}</TableCell>
                              <TableCell className="px-2 py-2 text-[11px] text-foreground text-right">{ticketHoursOf(t) != null ? ticketHoursOf(t)!.toFixed(1) : "—"}</TableCell>
                              <TableCell className="px-2 py-2 text-[11px] font-semibold text-foreground text-right">
                                {t.currency && t.currency !== "CAD" ? `${t.currency} ` : ""}{fmtCurrency(t.subtotal)}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-fieldiq-card/30 border-t-2 border-fieldiq-border">
                            <TableCell className="px-2 py-2 text-[11px] font-bold text-foreground" colSpan={5}>
                              Grand Total
                            </TableCell>
                            <TableCell className="px-2 py-2 text-[11px] font-bold text-foreground text-right">
                              {ticketHoursOf(gt) != null ? ticketHoursOf(gt)!.toFixed(1) : ""}
                            </TableCell>
                            <TableCell className="px-2 py-2 text-[11px] font-bold text-foreground text-right">
                              {gtDisplay}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  );
                })()}

                {/* Tickets & Revenue by Date — empty state */}
                {reportResults?.type === "rev-date" && byDateTickets.length === 0 && !reportResults._parseError && (
                  <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-4 text-center">
                    <p className="text-xs text-fieldiq-muted">No tickets in this period</p>
                  </div>
                )}

                {/* Revenue by Item Type — grouped by revenue_code */}
                {groups.length === 0 && rows.length > 0 && reportResults?.type === "rev-item-type" && (() => {
                  const grouped = rows.reduce<Record<string, any[]>>((acc, r) => {
                    const key = r.revenue_code;
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(r);
                    return acc;
                  }, {});
                  // The report total is `summary.total` — the same derivation
                  // the band reads — not a second sum over the same rows.
                  const allTotal = summary.total;
                  return (
                  <div className="space-y-4">
                    {Object.entries(grouped).map(([code, items]) => {
                      const subtotal = items.reduce((s: number, r: any) => s + Number(r.total), 0);
                      const sortedItems = applySortTo(items);
                      const serviceName = humanizeLabel(items[0].service_name);
                      return (
                      <div key={code} className="space-y-1">
                        <div className="flex items-center justify-between px-1">
                          <h4 className="text-[11px] font-bold text-foreground uppercase tracking-wide">
                            {code} — {serviceName}
                          </h4>
                          <span className="text-[10px] text-foreground">
                            {items.length} line{items.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="rounded-lg border border-fieldiq-border overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-fieldiq-card/50">
                                <SortTh label="Ticket #" field="ticket_number" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                                <SortTh label="Date" field="ticket_date" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                                <SortTh label="Customer" field="customer_name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                                <SortTh label="Description" field="description" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scHide} />
                                <SortTh label="Qty" field="quantity" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                                <SortTh label="Rate" field="unit_price" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                                <SortTh label="Amount" field="total" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {sortedItems.map((r: any, idx: number) => (
                                <TableRow key={`${r.ticket_number}-${idx}`} className="hover:bg-fieldiq-card/20 border-fieldiq-border">
                                  <TableCell className="px-2 py-1.5 text-[11px] font-medium text-fieldiq-accent">{r.ticket_number}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[10px] text-foreground">{r.ticket_date}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] text-foreground">{r.customer_name}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] text-foreground hidden sm:table-cell">{r.description ?? "—"}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] text-foreground text-right">{r.quantity.toLocaleString()}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] text-foreground text-right">{fmtCurrency(r.unit_price)}</TableCell>
                                  <TableCell className="px-2 py-1.5 text-[11px] font-semibold text-foreground text-right">{fmtCurrency(r.total)}</TableCell>
                                </TableRow>
                              ))}
                              <TableRow className="bg-fieldiq-card/30 border-t-2 border-fieldiq-border">
                                <TableCell className="px-2 py-2 text-[11px] font-bold text-foreground" colSpan={6}>
                                  Subtotal — {code} — {serviceName}
                                </TableCell>
                                <TableCell className="px-2 py-2 text-[11px] font-bold text-foreground text-right">
                                  {fmtCurrency(subtotal)}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                      );
                    })}
                    {allTotal != null && (
                      <div className="rounded-lg border-2 border-fieldiq-accent/30 bg-fieldiq-accent/5 px-4 py-3 flex items-center justify-between">
                        <span className="text-xs font-bold text-foreground uppercase tracking-wide">Grand Total</span>
                        <span className="text-sm font-bold text-fieldiq-accent">{fmtCurrency(allTotal)}</span>
                      </div>
                    )}
                  </div>
                  );
                })()}

                {/* Revenue by Item Type — empty state */}
                {groups.length === 0 && rows.length === 0 && reportResults?.type === "rev-item-type" && !reportResults._parseError && (
                  <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-4 text-center">
                    <p className="text-xs text-fieldiq-muted">No revenue in this period</p>
                  </div>
                )}

                {/* Revenue by Customer — grouped by customer_name */}
                {(reportResults?.type === "rev-customer" || reportResults?.type === "rev-customer-detail") && (reportResults.customers?.length > 0 || reportResults.jobs?.length > 0) && (() => {
                  const customers: any[] = reportResults.customers || [];
                  const jobs: any[] = reportResults.jobs || [];
                  // Group jobs by customer_name
                  const jobsByCustomer: Record<string, any[]> = {};
                  jobs.forEach((j: any) => {
                    const name = j.customer_name || "Unknown";
                    if (!jobsByCustomer[name]) jobsByCustomer[name] = [];
                    jobsByCustomer[name].push(j);
                  });
                  // Build ordered groups from customers summary
                  const customerGroups = customers.length > 0
                    ? customers.map((c: any) => ({
                        name: c.customer_name,
                        totalJobs: c.total_jobs,
                        totalRevenue: c.total_revenue,
                        outstanding: c.outstanding,
                        jobs: jobsByCustomer[c.customer_name] || [],
                      }))
                    : Object.entries(jobsByCustomer).map(([name, cJobs]) => ({
                        name,
                        totalJobs: cJobs.length,
                        totalRevenue: cJobs.reduce((sum: number, j: any) => sum + Number(j.job_revenue || 0), 0),
                        outstanding: 0,
                        jobs: cJobs,
                      }));
                  // The report total is `summary.total` — the same derivation
                  // the band reads — not a second sum over the same groups.
                  const reportTotal = summary.total;
                  return (
                    <div className="space-y-4">
                      {customerGroups.map((group, gi) => (
                        <div key={group.name || gi} className="space-y-1">
                          <div className="flex items-center justify-between px-1">
                            <h4 className="text-[11px] font-bold text-foreground uppercase tracking-wide">
                              {group.name}
                            </h4>
                            <span className="text-[10px] text-foreground">
                              {group.totalJobs} job{group.totalJobs !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <div className="rounded-lg border border-fieldiq-border overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-fieldiq-card/50">
                                  <SortTh label="Job #" field="job_number" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                                  <SortTh label="Well / Rig" field="well_rig" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                                  <SortTh label="Date" field="scheduled_date" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                                  <SortTh label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={sc} />
                                  <SortTh label="Revenue" field="job_revenue" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className={scR} />
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {applySortTo(group.jobs, (j, f) => {
                                  switch (f) {
                                    case 'well_rig': return [j.well_name, j.rig_name].filter(Boolean).join(' / ');
                                    case 'job_revenue': return Number(j.job_revenue || 0);
                                    default: return j[f];
                                  }
                                }).map((j: any, idx: number) => (
                                  <TableRow key={`${j.id}-${idx}`} className="hover:bg-fieldiq-card/20 border-fieldiq-border">
                                    <TableCell className="px-2 py-1.5 text-[11px] font-medium text-fieldiq-accent">{j.job_number || "—"}</TableCell>
                                    <TableCell className="px-2 py-1.5 text-[11px] text-foreground">{[j.well_name, j.rig_name].filter(Boolean).join(" / ") || "—"}</TableCell>
                                    <TableCell className="px-2 py-1.5 text-[10px] text-foreground">{calendarDayString(j.scheduled_date) || "—"}</TableCell>
                                    <TableCell className="px-2 py-1.5 text-[10px] text-foreground capitalize">{humanizeLabel(j.status || '') || "—"}</TableCell>
                                    <TableCell className="px-2 py-1.5 text-[11px] font-semibold text-foreground text-right">{fmtCurrency(j.job_revenue)}</TableCell>
                                  </TableRow>
                                ))}
                                <TableRow className="bg-fieldiq-card/30 border-t-2 border-fieldiq-border">
                                  <TableCell className="px-2 py-2 text-[11px] font-bold text-foreground" colSpan={4}>
                                    Subtotal — {group.name}
                                  </TableCell>
                                  <TableCell className="px-2 py-2 text-[11px] font-bold text-foreground text-right">
                                    {fmtCurrency(group.totalRevenue)}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      ))}
                      {reportTotal != null && (
                        <div className="rounded-lg border-2 border-fieldiq-accent/30 bg-fieldiq-accent/5 px-4 py-3 flex items-center justify-between">
                          <span className="text-xs font-bold text-foreground uppercase tracking-wide">Report Total</span>
                          <span className="text-sm font-bold text-fieldiq-accent">{fmtCurrency(reportTotal)}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Hours & Jobs by Employee — its own table; a row opens the
                    employee's page over the same range. */}
                {groups.length === 0 && rows.length > 0 && reportResults?.type === "ops-hours" && (
                  <CrewHoursTable
                    rows={applySortTo(rows)}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={toggleSort}
                    onOpen={(userId) => openRecord(crewDetailPath(userId, dateFrom, dateTo))}
                  />
                )}

                {/* Flat rows (ops, payroll, safety) */}
                {groups.length === 0 && rows.length > 0 && reportResults?.type !== "ops-hours" && reportResults?.type !== "ops-equipment" && reportResults?.type !== "rev-item-type" && reportResults?.type !== "rev-customer" && reportResults?.type !== "rev-customer-detail" && reportResults?.type !== "rev-date" && (() => {
                  const visibleCols = Object.keys(rows[0]).filter((k) => !isHiddenColumn(k));
                  const headerLabel = (key: string): string => formatColumnHeader(key);
                  const sorted = applySortTo(rows);
                  return (
                  <div className="rounded-lg border border-fieldiq-border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-fieldiq-card/50">
                          {visibleCols.map((key) => (
                              <SortTh
                                key={key}
                                label={headerLabel(key)}
                                field={key}
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSort={toggleSort}
                                className={typeof rows[0]?.[key] === 'number' ? scR : sc}
                              />
                            ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sorted.map((r: any, idx: number) => (
                          <TableRow key={idx} className="hover:bg-fieldiq-card/20 border-fieldiq-border">
                            {visibleCols.map((key) => (
                                <TableCell key={key} className={`px-2 py-2 text-[11px] text-foreground ${typeof r[key] === 'number' ? 'text-right' : ''}`}>
                                  {formatCellValue(key, r[key], timeZone)}
                                </TableCell>
                              ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  );
                })()}

                {/* Grand total for flat-row reports that include one.
                    EXCLUDES `rev-item-type`, WHICH RENDERS ITS OWN. That
                    report is flat rows this screen groups by revenue code, so
                    it satisfies `groups.length === 0 && rows.length > 0` and
                    reaches here as well as its own branch. It did not collide
                    before only because `grandTotalNum` read `grand_total`,
                    which this endpoint does not send, and so was 0 — the
                    suppression was the band bug holding the duplicate down.
                    Naming the type is the honest guard; `> 0` was never one. */}
                {groups.length === 0 && rows.length > 0 && grandTotalNum > 0 && reportResults?.type !== "ops-equipment" && reportResults?.type !== "rev-item-type" && (
                  <div className="rounded-lg border-2 border-fieldiq-accent/30 bg-fieldiq-accent/5 px-4 py-3 flex items-center justify-between">
                    <span className="text-xs font-bold text-foreground uppercase tracking-wide">Grand Total</span>
                    <span className="text-sm font-bold text-fieldiq-accent">{fmtCurrency(grandTotalNum)}</span>
                  </div>
                )}

                {/* Empty / parse states.

                    A THIRD "Report failed to load" branch used to stand here,
                    gated on `!reportResults`. Every branch in this block is
                    inside the `shouldFetch && reportResults && …` wrapper
                    above, so `reportResults` is truthy by construction and
                    that condition could never be true — a failure state with
                    no reachable input, which is why the runner appeared to
                    have an error branch while rendering none. The real one is
                    now the LoadFailureNotice above, outside this wrapper,
                    where a failed load actually arrives. */}
                {groups.length === 0 && rows.length === 0 && byDateTickets.length === 0 && reportResults && (reportResults._parseError || (!reportResults.rows && !reportResults.data && !reportResults.groups && !reportResults.customers && !reportResults.tickets && !reportResults.aging && !reportResults.monthly_trend)) && (
                  <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-4 text-center space-y-1">
                    {/* A warning triangle, not a success checkmark.

                        `text-fieldiq-accent-signal`, NOT `text-fi-signal-amber`:
                        the signal hues are fills to put dark ink on and have no
                        light twin, so as ink one reads 1.49 on a light surface.
                        This glyph sits on `bg-fieldiq-card`, which is #FFFFFF in
                        light mode, where accent-signal's #b45309 measures 5.02.
                        (The tab-strip caveat in CLAUDE.md is about the 4.36 it
                        reads on the PAGE surface `--fieldiq-dark`; this is the
                        card, not the page.) */}
                    <AlertTriangle className="w-6 h-6 text-fieldiq-accent-signal mx-auto" />
                    <p className="text-xs font-semibold text-foreground">
                      Could not parse report response. Check console for details.
                    </p>
                    {process.env.NODE_ENV === "development" && (
                      <p className="text-[10px] text-foreground font-mono">
                        Keys: {Object.keys(reportResults).join(", ")}
                      </p>
                    )}
                  </div>
                )}

                {groups.length === 0 && rows.length === 0 && byDateTickets.length === 0 && !(reportResults?.customers?.length > 0 || reportResults?.jobs?.length > 0) && reportResults && reportResults.type !== "rev-item-type" && reportResults.type !== "rev-date" && (reportResults.rows || reportResults.data || reportResults.groups || reportResults.customers || reportResults.jobs) && (
                  <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-4 text-center space-y-1">
                    <CheckCircle2 className="w-6 h-6 text-fieldiq-accent mx-auto" />
                    <p className="text-xs font-semibold text-foreground">
                      {reportResults?.message || "Report generated — no data for this period"}
                    </p>
                    <p className="text-[11px] text-foreground">
                      Try a wider date range or check that tickets exist for this period.
                    </p>
                  </div>
                )}
              </div>
            )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default UnifiedReports;
